import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import SignatureCanvas from 'react-signature-canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper function for smart pagination
const getPageNumbers = (currentPage, totalPages) => {
  const delta = 2; // Number of pages to show on each side of current page
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
      range.push(i);
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};

const AdminPanelNew = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('events'); // events, series, announcement, ads, validations, teams
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Check if user is admin
  useEffect(() => {
    if (user?.role !== 'ADMIN') {
      toast.error('Akses ditolak. Hanya admin yang dapat mengakses halaman ini.');
      navigate('/home');
    }
  }, [user, navigate]);

  const menuItems = [
    { id: 'events', label: 'Kelola Event', icon: '📅' },
    { id: 'series', label: 'Kelola Series', icon: '📚' },
    { id: 'sekolah', label: 'Kelola Sekolah', icon: '🏫' },
    { id: 'users', label: 'Kelola User', icon: '👤' },
    { id: 'transactions', label: 'Transaksi', icon: '💰' },
    { id: 'bap', label: 'BAP Pertandingan', icon: '📋' },
    { id: 'berita', label: 'Kelola Berita', icon: '📰' },
    { id: 'videos', label: 'Kelola Video', icon: '🎬' },
    { id: 'gallery', label: 'Kelola Galeri', icon: '🖼️' },
    { id: 'tickets', label: 'Kelola Tiket', icon: '🎫' },
    { id: 'faq', label: 'Kelola FAQ', icon: '❓' },
    { id: 'announcement', label: 'Edit Announcement', icon: '📢' },
    { id: 'ads', label: 'Kelola Iklan', icon: '🎯' },
    { id: 'validations', label: 'Validasi Registrasi', icon: '✅' },
    { id: 'teams', label: 'Tim', icon: '👥' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`bg-gradient-ltb-r text-white transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'} flex-shrink-0`}>
        <div className="flex flex-col h-screen sticky top-0">
          {/* Logo & Toggle */}
          <div className="p-4 flex items-center justify-between border-b border-white/20">
            {sidebarOpen && (
              <div>
                <h1 className="text-xl font-bold">Admin Panel</h1>
                <p className="text-xs text-white/70">LTB+</p>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-white/20 rounded-lg transition-all"
              title={sidebarOpen ? 'Tutup sidebar' : 'Buka sidebar'}
            >
              {sidebarOpen ? '◀' : '▶'}
            </button>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto py-4">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center px-4 py-3 transition-all ${
                  activeTab === item.id
                    ? 'bg-white/20 border-r-4 border-white'
                    : 'hover:bg-white/10'
                }`}
                title={!sidebarOpen ? item.label : ''}
              >
                <span className="text-2xl">{item.icon}</span>
                {sidebarOpen && (
                  <span className="ml-3 font-medium text-sm">{item.label}</span>
                )}
              </button>
            ))}
          </nav>

          {/* Back to Home */}
          <div className="p-4 border-t border-white/20">
            <button
              onClick={() => navigate('/home')}
              className="w-full bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center"
            >
              {sidebarOpen ? (
                <>
                  <span>← Kembali ke Home</span>
                </>
              ) : (
                <span>🏠</span>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 overflow-x-hidden">
        {/* Header */}
        <header className="bg-white shadow-md py-4 px-6 sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {menuItems.find(item => item.id === activeTab)?.label}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                {user?.nama} • {user?.role}
              </p>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'events' && <EventsManagement token={token} />}
          {activeTab === 'series' && <SeriesManagementPage token={token} />}
          {activeTab === 'sekolah' && <SekolahManagement token={token} />}
          {activeTab === 'users' && <UserManagement token={token} />}
          {activeTab === 'berita' && <BeritaManagement token={token} />}
          {activeTab === 'videos' && <VideoManagement token={token} />}
          {activeTab === 'gallery' && <GalleryManagement token={token} />}
          {activeTab === 'tickets' && <TicketManagement token={token} />}
          {activeTab === 'faq' && <FAQManagement token={token} />}
          {activeTab === 'announcement' && <AnnouncementManagement token={token} />}
          {activeTab === 'ads' && <AdsManagement token={token} />}
          {activeTab === 'validations' && <ValidationManagement token={token} />}
          {activeTab === 'teams' && <TeamsManagement token={token} />}
          {activeTab === 'transactions' && <TransactionManagement token={token} />}
          {activeTab === 'bap' && <BAPManagement token={token} />}
        </div>
      </div>
    </div>
  );
};

// ==================== EVENTS MANAGEMENT ====================
const EventsManagement = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [cities, setCities] = useState([]);
  const [jenjang, setJenjang] = useState([]);
  const [kategori, setKategori] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  
  const [formData, setFormData] = useState({
    nama: '',
    kategori: '',
    m_series_id: '',
    tgl_reg_mulai: '',
    tgl_reg_selesai: '',
    tgl_event_mulai: '',
    tgl_event_selesai: '',
    m_kota_id: '',
    m_jenjang_id: '',
    deskripsi: '',
    gambar_banner: '',
    cp_email: '',
    cp1_nama: '',
    cp1_whatsapp: '',
    cp2_nama: '',
    cp2_whatsapp: ''
  });

  useEffect(() => {
    fetchEvents();
    fetchMasterData();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?status_open=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [citiesRes, jenjangRes, kategoriRes, seriesRes] = await Promise.all([
        axios.get(`${API}/master/kota`),
        axios.get(`${API}/master/jenjang`),
        axios.get(`${API}/master/kategori`),
        axios.get(`${API}/series?aktif=true`)
      ]);
      setCities(citiesRes.data);
      setJenjang(jenjangRes.data);
      setKategori(kategoriRes.data);
      setSeriesList(seriesRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEvent) {
        // Update
        await axios.put(`${API}/admin/events/${editingEvent.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event berhasil diupdate');
      } else {
        // Create
        await axios.post(`${API}/admin/events`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event berhasil dibuat');
      }
      
      setShowForm(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan event');
    }
  };

  const handleUploadBanner = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/banner`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, gambar_banner: response.data.url });
      toast.success('Banner berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload banner');
    }
  };

  const handlePublish = async (eventId) => {
    try {
      await axios.post(`${API}/admin/events/${eventId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event berhasil dipublish');
      fetchEvents();
    } catch (error) {
      toast.error('Gagal publish event');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Yakin ingin menghapus event ini?')) return;
    
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event berhasil dihapus');
      fetchEvents();
    } catch (error) {
      toast.error('Gagal menghapus event');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    
    setFormData({
      nama: event.nama,
      kategori: event.kategori || '',
      m_series_id: event.m_series_id || '',
      tgl_reg_mulai: event.tgl_reg_mulai ? event.tgl_reg_mulai.split('T')[0] : '',
      tgl_reg_selesai: event.tgl_reg_selesai ? event.tgl_reg_selesai.split('T')[0] : '',
      tgl_event_mulai: event.tgl_event_mulai ? event.tgl_event_mulai.split('T')[0] : '',
      tgl_event_selesai: event.tgl_event_selesai ? event.tgl_event_selesai.split('T')[0] : '',
      m_kota_id: event.m_kota_id,
      m_jenjang_id: event.m_jenjang_id,
      deskripsi: event.description || '',
      gambar_banner: event.image_url || '',
      cp_email: event.cp_email || '',
      cp1_nama: event.cp1_nama || '',
      cp1_whatsapp: event.cp1_whatsapp || '',
      cp2_nama: event.cp2_nama || '',
      cp2_whatsapp: event.cp2_whatsapp || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      nama: '',
      kategori: '',
      m_series_id: '',
      tgl_reg_mulai: '',
      tgl_reg_selesai: '',
      tgl_event_mulai: '',
      tgl_event_selesai: '',
      m_kota_id: '',
      m_jenjang_id: '',
      deskripsi: '',
      gambar_banner: '',
      cp_email: '',
      cp1_nama: '',
      cp1_whatsapp: '',
      cp2_nama: '',
      cp2_whatsapp: ''
    });
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-end items-center mb-6">
        <button
          onClick={() => {
            setShowForm(true);
            setEditingEvent(null);
            resetForm();
          }}
          className="bg-ltb-bright text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
        >
          + Buat Event Baru
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-xl font-bold mb-4">
            {editingEvent ? 'Edit Event' : 'Buat Event Baru'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Nama Event */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Event <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nama}
                  onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              {/* Kategori */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kategori Event <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.kategori}
                  onChange={(e) => setFormData({ ...formData, kategori: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih kategori</option>
                  {kategori.map(k => (
                    <option key={k.id} value={k.nama}>{k.nama}</option>
                  ))}
                </select>
              </div>

              {/* Series */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Series <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.m_series_id}
                  onChange={(e) => setFormData({ ...formData, m_series_id: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih series</option>
                  {seriesList.map(s => (
                    <option key={s.id} value={s.id}>{s.nama_series}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Kelola series di menu "Kelola Series"
                </p>
              </div>

              {/* Tanggal Registrasi Mulai */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tanggal Registrasi Mulai <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.tgl_reg_mulai}
                  onChange={(e) => setFormData({ ...formData, tgl_reg_mulai: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              {/* Tanggal Registrasi Selesai */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tanggal Registrasi Selesai <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.tgl_reg_selesai}
                  onChange={(e) => setFormData({ ...formData, tgl_reg_selesai: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              {/* Tanggal Event Mulai */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tanggal Event Mulai <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.tgl_event_mulai}
                  onChange={(e) => setFormData({ ...formData, tgl_event_mulai: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              {/* Tanggal Event Selesai */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tanggal Event Selesai <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.tgl_event_selesai}
                  onChange={(e) => setFormData({ ...formData, tgl_event_selesai: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              {/* Kota */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kota <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.m_kota_id}
                  onChange={(e) => setFormData({ ...formData, m_kota_id: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih kota</option>
                  {cities.map(c => (
                    <option key={c.id} value={c.id}>{c.kota}</option>
                  ))}
                </select>
              </div>

              {/* Jenjang */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Jenjang <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.m_jenjang_id}
                  onChange={(e) => setFormData({ ...formData, m_jenjang_id: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih jenjang</option>
                  {jenjang.map(j => (
                    <option key={j.id} value={j.id}>{j.jenjang}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Deskripsi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deskripsi
              </label>
              <textarea
                value={formData.deskripsi}
                onChange={(e) => setFormData({ ...formData, deskripsi: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
            </div>

            {/* Upload Banner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gambar Banner
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadBanner}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {formData.gambar_banner && (
                <img src={formData.gambar_banner} alt="Banner" className="mt-2 h-32 rounded" />
              )}
            </div>

            {/* Contact Person Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CP Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.cp_email}
                onChange={(e) => setFormData({ ...formData, cp_email: e.target.value })}
                placeholder="email@example.com"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
            </div>

            {/* Contact Person 1 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CP 1 (Nama) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cp1_nama}
                onChange={(e) => setFormData({ ...formData, cp1_nama: e.target.value })}
                placeholder="Nama contact person 1"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CP 1 (Nomor WhatsApp) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cp1_whatsapp}
                onChange={(e) => setFormData({ ...formData, cp1_whatsapp: e.target.value })}
                placeholder="08123456789"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
            </div>

            {/* Contact Person 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CP 2 (Nama) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cp2_nama}
                onChange={(e) => setFormData({ ...formData, cp2_nama: e.target.value })}
                placeholder="Nama contact person 2"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CP 2 (Nomor WhatsApp) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cp2_whatsapp}
                onChange={(e) => setFormData({ ...formData, cp2_whatsapp: e.target.value })}
                placeholder="08123456789"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingEvent(null);
                  resetForm();
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-all"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-ltb-bright text-white rounded-lg hover:shadow-lg transition-all"
              >
                {editingEvent ? 'Update Event' : 'Simpan Event'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Events List */}
      <div className="grid gap-4">
        {events.map((event) => (
          <div key={event.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800">{event.nama}</h3>
                <p className="text-gray-600 mt-1">{event.kategori}</p>
                <p className="text-sm text-gray-500 mt-2">
                  📅 Event: {event.tgl_event_mulai} s/d {event.tgl_event_selesai}
                </p>
                <p className="text-sm text-gray-500">
                  📝 Registrasi: {event.tgl_reg_mulai} s/d {event.tgl_reg_selesai}
                </p>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
                  event.status_open ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {event.status_open ? 'Published' : 'Draft'}
                </span>
              </div>
              <div className="flex space-x-2">
                {!event.status_open && (
                  <button
                    onClick={() => handlePublish(event.id)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all"
                  >
                    Publish
                  </button>
                )}
                <button
                  onClick={() => handleEdit(event)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(event.id)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all"
                >
                  Hapus
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ==================== BERITA MANAGEMENT ====================
const BeritaManagement = ({ token }) => {
  const [berita, setBerita] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBerita, setEditingBerita] = useState(null);
  
  const [formData, setFormData] = useState({
    judul: '',
    isi_berita: '',
    gambar_url: '',
    tanggal_publish: '',
    is_published: false,
    tags: []
  });

  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchBerita();
  }, []);

  const fetchBerita = async () => {
    try {
      const response = await axios.get(`${API}/admin/berita`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBerita(response.data);
    } catch (error) {
      console.error('Error fetching berita:', error);
      toast.error('Gagal memuat berita');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingBerita) {
        await axios.put(`${API}/admin/berita/${editingBerita.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Berita berhasil diupdate');
      } else {
        await axios.post(`${API}/admin/berita`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Berita berhasil dibuat');
      }
      
      setShowForm(false);
      setEditingBerita(null);
      resetForm();
      fetchBerita();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan berita');
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    await toast.promise(
      axios.post(`${API}/upload/banner`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }),
      {
        loading: 'Uploading gambar...',
        success: (response) => {
          setFormData({ ...formData, gambar_url: response.data.url });
          return 'Gambar berhasil diupload';
        },
        error: 'Gagal upload gambar',
      }
    );
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleEdit = (beritaItem) => {
    setEditingBerita(beritaItem);
    setFormData({
      judul: beritaItem.judul,
      isi_berita: beritaItem.isi_berita,
      gambar_url: beritaItem.gambar_url || '',
      tanggal_publish: beritaItem.tanggal_publish ? beritaItem.tanggal_publish.split('T')[0] : '',
      is_published: beritaItem.is_published,
      tags: beritaItem.t_berita_tags ? beritaItem.t_berita_tags.map(t => t.tag_name) : []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus berita ini?')) return;

    try {
      await axios.delete(`${API}/admin/berita/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Berita berhasil dihapus');
      fetchBerita();
    } catch (error) {
      toast.error('Gagal menghapus berita');
    }
  };

  const resetForm = () => {
    setFormData({
      judul: '',
      isi_berita: '',
      gambar_url: '',
      tanggal_publish: '',
      is_published: false,
      tags: []
    });
    setTagInput('');
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Add Button */}
      {showForm ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {editingBerita ? 'Edit Berita' : 'Tambah Berita Baru'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Judul <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.judul}
                onChange={(e) => setFormData({ ...formData, judul: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gambar Berita
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadImage}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {formData.gambar_url && (
                <img src={formData.gambar_url} alt="Preview" className="mt-2 h-32 rounded" />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Isi Berita <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <MDEditor
                  value={formData.isi_berita}
                  onChange={(content) => setFormData({ ...formData, isi_berita: content || '' })}
                  preview="edit"
                  height={300}
                  data-color-mode="light"
                  hideToolbar={false}
                  visibleDragBar={false}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tanggal Publish
              </label>
              <input
                type="date"
                value={formData.tanggal_publish}
                onChange={(e) => setFormData({ ...formData, tanggal_publish: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">Kosongkan untuk publish sekarang</p>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-4 h-4 text-ltb-bright border-gray-300 rounded focus:ring-ltb-bright"
                />
                <span className="text-sm font-medium text-gray-700">Publish langsung</span>
              </label>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ketik tag dan tekan Enter"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium transition-all"
              >
                {editingBerita ? 'Update Berita' : 'Simpan Berita'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingBerita(null);
                  resetForm();
                }}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-all"
              >
                Batal
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium transition-all flex items-center"
          >
            <span className="mr-2">+</span>
            Tambah Berita
          </button>
        </div>
      )}

      {/* Berita List */}
      <div className="space-y-4">
        {berita.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-600">Belum ada berita. Silakan tambahkan berita baru.</p>
          </div>
        ) : (
          berita.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{item.judul}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      item.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {item.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  
                  {item.gambar_url && (
                    <img 
                      src={item.gambar_url} 
                      alt={item.judul}
                      className="w-32 h-20 object-cover rounded mb-2"
                    />
                  )}
                  
                  <p className="text-gray-600 text-sm mb-2">{item.isi_berita.substring(0, 200)}...</p>
                  
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>📅 {new Date(item.tanggal_publish).toLocaleDateString('id-ID')}</span>
                    {item.t_berita_tags && item.t_berita_tags.length > 0 && (
                      <div className="flex space-x-1">
                        {item.t_berita_tags.map((tag) => (
                          <span key={tag.tag_name} className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded text-xs">
                            #{tag.tag_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all"
                  >
                    Hapus
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};


// ==================== VALIDATION MANAGEMENT ====================
const ValidationManagement = ({ token }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReg, setSelectedReg] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterEvent, setFilterEvent] = useState('');
  const [events, setEvents] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch events once
  useEffect(() => {
    fetchEvents();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch data when filters or page changes
  useEffect(() => {
    fetchRegistrations();
  }, [filterStatus, filterEvent, searchTerm, currentPage]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/registrations?page=${currentPage}&limit=${itemsPerPage}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterEvent) url += `&event_id=${filterEvent}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRegistrations(response.data.data || []);
      setTotalItems(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Gagal memuat registrasi');
    } finally {
      setLoading(false);
    }
  };

  const handleValidation = async (registrationId, status, catatan = '') => {
    const statusLabels = {
      'approved': 'menyetujui',
      'rejected': 'menolak',
      'pending': 'mengembalikan ke pending'
    };
    
    if (!window.confirm(`Yakin ingin ${statusLabels[status] || status} registrasi ini?`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/admin/registrations/${registrationId}/validate`,
        { status, catatan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const successLabels = {
        'approved': 'disetujui',
        'rejected': 'ditolak',
        'pending': 'dikembalikan ke pending'
      };
      toast.success(`Registrasi berhasil ${successLabels[status] || status}`);
      setShowDetailModal(false);
      fetchRegistrations();
    } catch (error) {
      toast.error('Gagal memvalidasi registrasi');
    }
  };

  const openDetailModal = (reg) => {
    setSelectedReg(reg);
    setShowDetailModal(true);
  };

  const handleDelete = async (registrationId) => {
    if (!window.confirm('Yakin ingin MENGHAPUS registrasi ini? Data berkas dan transaksi terkait juga akan dihapus. Tindakan ini tidak bisa dibatalkan.')) {
      return;
    }
    try {
      await axios.delete(
        `${API}/admin/registrations/${registrationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Registrasi berhasil dihapus');
      setShowDetailModal(false);
      fetchRegistrations();
    } catch (error) {
      toast.error('Gagal menghapus registrasi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.loading('Mengunduh data...');
      let url = `${API}/admin/export/registrations?`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterEvent) url += `event_id=${filterEvent}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `data_registrasi_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.dismiss();
      toast.success('Data berhasil diunduh');
    } catch (error) {
      toast.dismiss();
      toast.error('Gagal mengunduh data');
      console.error('Export error:', error);
    }
  };

  // Pagination numbers
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <div>
      {/* Header with Export Button */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Validasi Registrasi</h2>
          <p className="text-sm text-gray-500">Total: {totalItems.toLocaleString()} registrasi</p>
        </div>
        <button
          onClick={handleExportExcel}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export Excel
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Semua Status</option>
              <option value="pending">Pending</option>
              <option value="validated">Validated</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter Event
            </label>
            <select
              value={filterEvent}
              onChange={(e) => { setFilterEvent(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Semua Event</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>{event.nama}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cari Registrasi
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Ketik nama, email, atau sekolah..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
              <svg 
                className="absolute left-3 top-3 w-4 h-4 text-gray-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {loading && searchInput && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Registrations Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-500">Memuat data...</p>
        </div>
      ) : registrations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
          {searchTerm ? 'Tidak ada registrasi yang cocok dengan pencarian' : 'Tidak ada registrasi dengan filter yang dipilih'}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">No</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nama</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nama Sekolah</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Event</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Kompetisi</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {registrations.map((reg, index) => (
                    <tr key={reg.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-800">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{reg.m_personal?.nama || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{reg.m_personal?.email || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{reg.m_personal?.m_sekolah?.nama_sekolah || reg.data_utama?.nama_sekolah || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{reg.m_event?.nama || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {reg.m_kompetisi?.nama_kompetisi || 'N/A'}
                        <span className="text-xs text-gray-500 block">({reg.m_kompetisi?.jenis || 'N/A'})</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{reg.role_kompetisi}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                          reg.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          reg.status === 'validated' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {reg.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-center flex-wrap">
                          <button
                            onClick={() => openDetailModal(reg)}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-all"
                            title="Lihat Detail"
                          >
                            Detail
                          </button>
                          
                          {/* Pending: bisa Approve atau Reject */}
                          {reg.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleValidation(reg.id, 'approved')}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-all"
                                title="Setujui"
                              >
                                ✓ Approve
                              </button>
                              <button
                                onClick={() => handleValidation(reg.id, 'rejected')}
                                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-all"
                                title="Tolak"
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}
                          
                          {/* Validated: bisa ubah ke Pending atau Reject */}
                          {reg.status === 'validated' && (
                            <>
                              <button
                                onClick={() => handleValidation(reg.id, 'pending')}
                                className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600 transition-all"
                                title="Kembalikan ke Pending"
                              >
                                ↩ Pending
                              </button>
                              <button
                                onClick={() => handleValidation(reg.id, 'rejected')}
                                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-all"
                                title="Tolak"
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}
                          
                          {/* Rejected: bisa ubah ke Pending atau Approve */}
                          {reg.status === 'rejected' && (
                            <>
                              <button
                                onClick={() => handleValidation(reg.id, 'pending')}
                                className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600 transition-all"
                                title="Kembalikan ke Pending"
                              >
                                ↩ Pending
                              </button>
                              <button
                                onClick={() => handleValidation(reg.id, 'approved')}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-all"
                                title="Setujui"
                              >
                                ✓ Approve
                              </button>
                            </>
                          )}
                          
                          <button
                            onClick={() => handleDelete(reg.id)}
                            className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-all"
                            title="Hapus Registrasi"
                            data-testid={`delete-reg-${reg.id}`}
                          >
                            Hapus
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                Menampilkan {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} dari {totalItems.toLocaleString()} registrasi
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Prev
                </button>
                
                {pageNumbers.map((number, index) => (
                  <button
                    key={index}
                    onClick={() => typeof number === 'number' && setCurrentPage(number)}
                    disabled={number === '...'}
                    className={`px-4 py-2 border rounded-lg ${
                      currentPage === number
                        ? 'bg-ltb-bright text-white'
                        : number === '...'
                        ? 'cursor-default'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    {number}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedReg && (
        <ValidationDetailModal
          registration={selectedReg}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedReg(null);
          }}
          onValidate={handleValidation}
        />
      )}
    </div>
  );
};

// ==================== VALIDATION DETAIL MODAL ====================
const ValidationDetailModal = ({ registration, onClose, onValidate }) => {
  const [validating, setValidating] = useState(false);

  const handleSubmitValidation = async (status) => {
    setValidating(true);
    await onValidate(registration.id, status, ''); // Empty catatan
    setValidating(false);
  };

  const dataUtama = registration.data_utama || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Detail Registrasi</h2>
            <p className="text-gray-600 mt-1">{registration.m_personal?.nama}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-3xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Personal Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-bold text-lg mb-3">Informasi Peserta</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-600">Nama:</p>
                <p className="font-semibold">{registration.m_personal?.nama || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600">Email:</p>
                <p className="font-semibold">{registration.m_personal?.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600">No. HP:</p>
                <p className="font-semibold">{registration.m_personal?.no_telp || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Event Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-bold text-lg mb-3">Informasi Event</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-600">Event:</p>
                <p className="font-semibold">{registration.m_event?.nama || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600">Series:</p>
                <p className="font-semibold">{registration.m_series?.nama_series || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600">Kompetisi:</p>
                <p className="font-semibold">{registration.m_kompetisi?.nama_kompetisi || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600">Jenis:</p>
                <p className="font-semibold">{registration.m_kompetisi?.jenis || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600">Role:</p>
                <p className="font-semibold">{registration.role_kompetisi}</p>
              </div>
              <div>
                <p className="text-gray-600">Status:</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                  registration.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  registration.status === 'validated' ? 'bg-green-100 text-green-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {registration.status}
                </span>
              </div>
            </div>
          </div>

          {/* Registration Data */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-bold text-lg mb-3">Data Registrasi</h3>
            <div className="space-y-4">
              {/* Peran */}
              {dataUtama.peran && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600">Peran:</p>
                  <p className="font-semibold">{dataUtama.peran}</p>
                </div>
              )}

              {/* Nama Sekolah */}
              {dataUtama.nama_sekolah && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600">Nama Sekolah:</p>
                  <p className="font-semibold">{dataUtama.nama_sekolah}</p>
                </div>
              )}

              {/* Nama Tim Sekolah */}
              {dataUtama.nama_tim_sekolah && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600">Nama Tim Sekolah:</p>
                  <p className="font-semibold">{dataUtama.nama_tim_sekolah}</p>
                </div>
              )}

              {/* Kategori Skill */}
              {dataUtama.kategori_skill && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600">Kategori Skill:</p>
                  <p className="font-semibold">{dataUtama.kategori_skill}</p>
                </div>
              )}

              {/* Prestasi */}
              {dataUtama.prestasi && dataUtama.prestasi.length > 0 && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600 mb-2">Prestasi:</p>
                  <div className="space-y-3">
                    {dataUtama.prestasi.map((p, idx) => (
                      <div key={idx} className="bg-white p-3 rounded border">
                        <p className="font-semibold text-sm">{p.nama || 'N/A'} ({p.tahun || 'N/A'})</p>
                        {p.sertifikat_url && (
                          <a 
                            href={p.sertifikat_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm underline mt-1 block"
                          >
                            {p.sertifikat_url}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Media Sosial Tim */}
              {dataUtama.media_sosial_tim && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600">Media Sosial Tim:</p>
                  <p className="font-semibold">{dataUtama.media_sosial_tim}</p>
                </div>
              )}

              {/* Media Sosial Supporter */}
              {dataUtama.media_sosial_supporter && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600">Media Sosial Supporter:</p>
                  <p className="font-semibold">{dataUtama.media_sosial_supporter}</p>
                </div>
              )}

              {/* Media Sosial Crew */}
              {dataUtama.media_sosial_crew && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600">Media Sosial Crew:</p>
                  <p className="font-semibold">{dataUtama.media_sosial_crew}</p>
                </div>
              )}

              {/* Media Sosial Sekolah */}
              {dataUtama.media_sosial_sekolah && (
                <div className="border-b pb-2">
                  <p className="text-sm text-gray-600">Media Sosial Sekolah:</p>
                  <p className="font-semibold">{dataUtama.media_sosial_sekolah}</p>
                </div>
              )}
            </div>
          </div>

          {/* Validation Section */}
          {registration.status === 'pending' && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-3">Validasi Registrasi</h3>
              <div className="flex gap-3">
                <button
                  onClick={() => handleSubmitValidation('approved')}
                  disabled={validating}
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                >
                  ✓ Approve Registrasi
                </button>
                <button
                  onClick={() => handleSubmitValidation('rejected')}
                  disabled={validating}
                  className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-all disabled:opacity-50"
                >
                  ✗ Reject Registrasi
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== SERIES MANAGEMENT PAGE ====================
const SeriesManagementPage = ({ token }) => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  
  const [formData, setFormData] = useState({
    nama_series: '',
    kota: '',
    deskripsi: '',
    booklet_url: '',
    aktif: true
  });

  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/series`);
      setSeries(response.data);
    } catch (error) {
      console.error('Error fetching series:', error);
      toast.error('Gagal memuat data series');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSeries) {
        // Update
        await axios.put(`${API}/admin/series/${editingSeries.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Series berhasil diupdate');
      } else {
        // Create
        await axios.post(`${API}/admin/series`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Series berhasil ditambahkan');
      }
      
      setShowForm(false);
      setEditingSeries(null);
      resetForm();
      fetchSeries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan series');
    }
  };

  const handleUploadBooklet = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('File harus berupa PDF');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    // Use toast.promise for automatic loading/success/error handling
    await toast.promise(
      axios.post(`${API}/upload/booklet`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }),
      {
        loading: 'Uploading booklet...',
        success: (response) => {
          setFormData({ ...formData, booklet_url: response.data.url });
          return 'Booklet berhasil diupload';
        },
        error: 'Gagal upload booklet',
      }
    );
  };

  const handleEdit = (s) => {
    setEditingSeries(s);
    setFormData({
      nama_series: s.nama_series,
      kota: s.kota || '',
      deskripsi: s.deskripsi || '',
      booklet_url: s.booklet_url || '',
      aktif: s.aktif
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus series ini? Series yang sudah digunakan oleh event tidak bisa dihapus.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/series/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Series berhasil dihapus');
      fetchSeries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus series');
    }
  };

  const resetForm = () => {
    setFormData({
      nama_series: '',
      kota: '',
      deskripsi: '',
      booklet_url: '',
      aktif: true
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Add/Edit Form */}
      {showForm ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {editingSeries ? 'Edit Series' : 'Tambah Series Baru'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Series <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nama_series}
                onChange={(e) => setFormData({ ...formData, nama_series: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                placeholder="Contoh: Yamaha Fazzio LTB With Tri 2026 Surabaya Series"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kota <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.kota}
                onChange={(e) => setFormData({ ...formData, kota: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                placeholder="Contoh: Surabaya, Gresik, Mojokerto"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deskripsi
              </label>
              <textarea
                value={formData.deskripsi}
                onChange={(e) => setFormData({ ...formData, deskripsi: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                rows="3"
                placeholder="Deskripsi series (opsional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Booklet / Regulasi (PDF)
              </label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleUploadBooklet}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {formData.booklet_url && (
                <div className="mt-2">
                  <a 
                    href={formData.booklet_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    📄 Lihat Booklet
                  </a>
                </div>
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.aktif}
                  onChange={(e) => setFormData({ ...formData, aktif: e.target.checked })}
                  className="w-4 h-4 text-ltb-bright border-gray-300 rounded focus:ring-ltb-bright"
                />
                <span className="text-sm font-medium text-gray-700">Aktif</span>
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium transition-all"
              >
                {editingSeries ? 'Update Series' : 'Simpan Series'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingSeries(null);
                  resetForm();
                }}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-all"
              >
                Batal
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium transition-all flex items-center"
          >
            <span className="mr-2">+</span>
            Tambah Series
          </button>
        </div>
      )}

      {/* Series List */}
      <div className="space-y-4">
        {series.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-600">Belum ada series. Silakan tambahkan series baru.</p>
          </div>
        ) : (
          series.map((s) => (
            <div key={s.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-xl font-bold text-gray-800">{s.nama_series}</h3>
                    {s.kota && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        {s.kota}
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      s.aktif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {s.aktif ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </div>
                  
                  {s.deskripsi && (
                    <p className="text-gray-600 mt-2">{s.deskripsi}</p>
                  )}
                  
                  {s.booklet_url && (
                    <div className="mt-3">
                      <a 
                        href={s.booklet_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:underline"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Booklet / Regulasi
                      </a>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-3">
                    Created: {new Date(s.created_at).toLocaleDateString('id-ID')}
                  </p>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(s)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all"
                  >
                    Hapus
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ==================== ANNOUNCEMENT MANAGEMENT ====================
const AnnouncementManagement = ({ token }) => {
  const [announcement, setAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    gambar_url: '',
    gambar_mobile: '',
    deskripsi: ''
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);

  useEffect(() => {
    fetchAnnouncement();
  }, []);

  const fetchAnnouncement = async () => {
    try {
      const response = await axios.get(`${API}/announcements/active`);
      if (response.data) {
        setAnnouncement(response.data);
        setFormData({
          gambar_url: response.data.gambar_url,
          gambar_mobile: response.data.gambar_mobile || '',
          deskripsi: response.data.deskripsi || ''
        });
      }
    } catch (error) {
      console.error('Error fetching announcement:', error);
    }
  };

  const handleUploadDesktop = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/banner`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, gambar_url: response.data.url });
      toast.success('Gambar desktop berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload gambar desktop');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadMobile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingMobile(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/banner`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, gambar_mobile: response.data.url });
      toast.success('Gambar mobile berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload gambar mobile');
    } finally {
      setUploadingMobile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formDataSubmit = new FormData();
      formDataSubmit.append('gambar_url', formData.gambar_url);
      formDataSubmit.append('gambar_mobile', formData.gambar_mobile);
      formDataSubmit.append('deskripsi', formData.deskripsi);

      await axios.post(`${API}/admin/announcements`, formDataSubmit, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Announcement berhasil diupdate');
      fetchAnnouncement();
    } catch (error) {
      toast.error('Gagal update announcement');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Preview Desktop */}
        {formData.gambar_url && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview Desktop:</label>
            <img 
              src={formData.gambar_url} 
              alt="Announcement Desktop" 
              className="w-full h-64 object-cover rounded-lg border-2 border-gray-200"
            />
          </div>
        )}

        {/* Preview Mobile */}
        {formData.gambar_mobile && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview Mobile:</label>
            <div className="max-w-sm mx-auto">
              <img 
                src={formData.gambar_mobile} 
                alt="Announcement Mobile" 
                className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
              />
            </div>
          </div>
        )}

        {/* Upload Gambar Desktop */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Upload Gambar Announcement (Desktop) <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleUploadDesktop}
            disabled={uploading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
          <p className="text-sm text-gray-500 mt-1">Recommended: 1920x400px, max 5MB</p>
        </div>

        {/* Upload Gambar Mobile */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Upload Gambar Announcement (Mobile) <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleUploadMobile}
            disabled={uploadingMobile}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
          <p className="text-sm text-gray-500 mt-1">Recommended: 800x300px, max 2MB</p>
        </div>

        {/* Deskripsi */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Deskripsi
          </label>
          <textarea
            value={formData.deskripsi}
            onChange={(e) => setFormData({ ...formData, deskripsi: e.target.value })}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
            placeholder="Deskripsi singkat announcement (optional)"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!formData.gambar_url || !formData.gambar_mobile || uploading || uploadingMobile}
          className="w-full bg-ltb-bright text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading || uploadingMobile ? 'Uploading...' : 'Update Announcement'}
        </button>
      </form>
    </div>
  );
};

// ==================== ADS MANAGEMENT ====================
const AdsManagement = ({ token }) => {
  const [ads, setAds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState({
    gambar_url: '',
    gambar_mobile: '',
    link: ''
  });

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/admin/ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAds(response.data);
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
  };

  const handleUploadDesktop = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const uploadResponse = await axios.post(`${API}/upload/banner`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData({ ...formData, gambar_url: uploadResponse.data.url });
      toast.success('Gambar desktop berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload gambar desktop');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadMobile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const uploadResponse = await axios.post(`${API}/upload/banner`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData({ ...formData, gambar_mobile: uploadResponse.data.url });
      toast.success('Gambar mobile berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload gambar mobile');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitNewAd = async (e) => {
    e.preventDefault();
    
    try {
      const formDataAd = new FormData();
      formDataAd.append('gambar_url', formData.gambar_url);
      formDataAd.append('gambar_mobile', formData.gambar_mobile);
      formDataAd.append('link', formData.link);
      formDataAd.append('urutan', ads.length + 1);

      await axios.post(`${API}/admin/ads`, formDataAd, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Iklan berhasil ditambahkan');
      setShowForm(false);
      resetForm();
      fetchAds();
    } catch (error) {
      toast.error('Gagal menambahkan iklan');
    }
  };

  const handleToggle = async (adId) => {
    try {
      await axios.put(`${API}/admin/ads/${adId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status iklan berhasil diubah');
      fetchAds();
    } catch (error) {
      toast.error('Gagal mengubah status iklan');
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Yakin ingin menghapus iklan ini?')) return;
    
    try {
      await axios.delete(`${API}/admin/ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Iklan berhasil dihapus');
      fetchAds();
    } catch (error) {
      toast.error('Gagal menghapus iklan');
    }
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad);
    setFormData({
      gambar_url: ad.gambar_url,
      gambar_mobile: ad.gambar_mobile || '',
      link: ad.link || ''
    });
  };

  const handleUpdateAd = async (adId) => {
    try {
      const formDataUpdate = new FormData();
      formDataUpdate.append('gambar_mobile', formData.gambar_mobile);
      formDataUpdate.append('link', formData.link);

      await axios.put(`${API}/admin/ads/${adId}`, formDataUpdate, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Iklan berhasil diupdate');
      setEditingAd(null);
      resetForm();
      fetchAds();
    } catch (error) {
      toast.error('Gagal update iklan');
    }
  };

  const resetForm = () => {
    setFormData({
      gambar_url: '',
      gambar_mobile: '',
      link: ''
    });
    setEditingAd(null);
  };

  return (
    <div>
      {/* Add New Ad Form */}
      {showForm ? (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Tambah Iklan Baru</h3>
          
          <form onSubmit={handleSubmitNewAd} className="space-y-4">
            {/* Preview Desktop */}
            {formData.gambar_url && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preview Desktop:</label>
                <img 
                  src={formData.gambar_url} 
                  alt="Preview Desktop" 
                  className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                />
              </div>
            )}

            {/* Preview Mobile */}
            {formData.gambar_mobile && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preview Mobile:</label>
                <div className="max-w-sm mx-auto">
                  <img 
                    src={formData.gambar_mobile} 
                    alt="Preview Mobile" 
                    className="w-full h-40 object-cover rounded-lg border-2 border-gray-200"
                  />
                </div>
              </div>
            )}

            {/* Upload Desktop */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gambar Desktop <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadDesktop}
                disabled={uploading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 1920x400px</p>
            </div>

            {/* Upload Mobile */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gambar Mobile <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadMobile}
                disabled={uploading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 800x300px</p>
            </div>

            {/* Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link URL (Opsional)
              </label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={!formData.gambar_url || !formData.gambar_mobile || uploading}
                className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Simpan Iklan'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
              >
                Batal
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex justify-end mb-6">
          <button
            onClick={() => setShowForm(true)}
            className="bg-ltb-bright text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center"
          >
            <span className="mr-2">+</span>
            Upload Iklan Baru
          </button>
        </div>
      )}

      {/* Ads Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ads.map((ad, index) => (
          <div key={ad.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Desktop Image */}
            <div>
              <p className="text-xs font-semibold text-gray-600 px-4 pt-2">Desktop:</p>
              <img 
                src={ad.gambar_url} 
                alt={`Ad Desktop ${index + 1}`} 
                className="w-full h-32 object-cover"
              />
            </div>
            
            {/* Mobile Image */}
            {ad.gambar_mobile && (
              <div>
                <p className="text-xs font-semibold text-gray-600 px-4 pt-2">Mobile:</p>
                <img 
                  src={ad.gambar_mobile} 
                  alt={`Ad Mobile ${index + 1}`} 
                  className="w-full h-24 object-cover"
                />
              </div>
            )}

            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-600">Urutan: {ad.urutan}</span>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  ad.aktif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {ad.aktif ? 'Aktif' : 'Nonaktif'}
                </span>
              </div>

              {/* Edit Mode */}
              {editingAd?.id === ad.id ? (
                <div className="space-y-3 mb-3">
                  {/* Upload Mobile Image */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Update Gambar Mobile
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadMobile}
                      disabled={uploading}
                      className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Link */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Link URL
                    </label>
                    <input
                      type="url"
                      value={formData.link}
                      onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                      placeholder="https://example.com"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleUpdateAd(ad.id)}
                      disabled={uploading}
                      className="flex-1 px-2 py-1 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                    >
                      {uploading ? 'Saving...' : 'Simpan'}
                    </button>
                    <button
                      onClick={() => resetForm()}
                      className="flex-1 px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs font-semibold hover:bg-gray-400"
                    >
                      Batal
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-600">Link:</span>
                    <button
                      onClick={() => handleEditAd(ad)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      ✏️ Edit
                    </button>
                  </div>
                  <div className="text-xs text-gray-800 break-all">
                    {ad.link ? (
                      <a href={ad.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {ad.link}
                      </a>
                    ) : (
                      <span className="text-gray-400 italic">Belum ada link</span>
                    )}
                  </div>
                  {!ad.gambar_mobile && (
                    <p className="text-xs text-yellow-600 mt-2">⚠️ Belum ada gambar mobile</p>
                  )}
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={() => handleToggle(ad.id)}
                  className={`flex-1 px-3 py-2 rounded text-sm font-semibold transition-all ${
                    ad.aktif 
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {ad.aktif ? 'Nonaktifkan' : 'Aktifkan'}
                </button>
                <button
                  onClick={() => handleDelete(ad.id)}
                  className="px-3 py-2 bg-red-600 text-white rounded text-sm font-semibold hover:bg-red-700 transition-all"
                >
                  Hapus
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {ads.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Belum ada iklan. Klik tombol "Upload Iklan Baru" untuk menambahkan.
        </div>
      )}
    </div>
  );
};



// ==================== TEAMS MANAGEMENT ====================
const TeamsManagement = ({ token }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterEvent, setFilterEvent] = useState('');
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
    fetchTeams();
  }, [filterEvent]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchTeams = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/teams`;
      if (filterEvent) url += `?event_id=${filterEvent}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      toast.error('Gagal memuat data tim');
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = (team) => {
    setSelectedTeam(team);
    setShowDetailModal(true);
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      {/* Filter */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Filter Event
        </label>
        <select
          value={filterEvent}
          onChange={(e) => setFilterEvent(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg"
        >
          <option value="">Semua Event</option>
          {events.map(event => (
            <option key={event.id} value={event.id}>{event.nama}</option>
          ))}
        </select>
      </div>

      {/* Teams Table */}
      {teams.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Tidak ada tim yang sudah submit berkas tambahan
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nama Tim</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nama Sekolah</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Kompetisi</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Jumlah Anggota</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Penanggungjawab</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {teams.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-semibold text-gray-800">{team.nama_tim}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{team.nama_sekolah}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{team.t_registrasi?.m_event?.nama || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {team.t_registrasi?.m_kompetisi?.nama_kompetisi || 'N/A'}
                      <span className="text-xs text-gray-500 block">
                        ({team.t_registrasi?.m_kompetisi?.jenis || 'N/A'})
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {team.my_team_data?.length || 0} orang
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {team.penanggungjawab_nama}
                      <span className="text-xs text-gray-500 block">{team.penanggungjawab_hp}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => openDetailModal(team)}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-all"
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedTeam && (
        <TeamDetailModal
          team={selectedTeam}
          token={token}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTeam(null);
          }}
        />
      )}
    </div>
  );
};

// ==================== MEMBER PROFILE MODAL ====================
const MemberProfileModal = ({ userId, onClose, token }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showAdjustBalance, setShowAdjustBalance] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Gagal mengambil data profil');
      }

      const data = await response.json();
      setProfile(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/transactions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchProfile();
      fetchTransactions();
    }
  }, [userId, token]);

  const handleAdjustBalance = async (e) => {
    e.preventDefault();
    if (!adjustAmount || !adjustReason.trim()) {
      toast.error('Mohon isi jumlah dan alasan');
      return;
    }

    try {
      setAdjusting(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/adjust-balance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: parseInt(adjustAmount),
          reason: adjustReason.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Gagal mengubah saldo');
      }

      toast.success(data.message);
      setShowAdjustBalance(false);
      setAdjustAmount('');
      setAdjustReason('');
      // Refresh data
      fetchProfile();
      fetchTransactions();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdjusting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
  };

  const getTransactionStatusBadge = (status) => {
    const styles = {
      success: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      expired: 'bg-gray-100 text-gray-700',
      failed: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const isAdminTransaction = (trans) => {
    return trans.order_id?.startsWith('ADMIN-') || 
           trans.payment_type?.startsWith('admin_adjustment');
  };

  const getAdminTransactionInfo = (trans) => {
    if (!isAdminTransaction(trans)) return null;
    
    // Parse order_id: ADMIN-{CREDIT/DEBIT}-{UUID}-{reason}
    const parts = trans.order_id?.split('-') || [];
    const type = parts[1]; // CREDIT or DEBIT
    const reason = parts.slice(3).join('-').replace(/_/g, ' ') || 'Penyesuaian saldo';
    const adminName = trans.payment_type?.replace('admin_adjustment_by_', '') || 'Admin';
    
    return { type, reason, adminName };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-6 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Profil Anggota</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-3xl"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Memuat data profil...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
            </div>
          ) : profile ? (
            <div className="space-y-6">
              {/* Photo & Basic Info */}
              <div className="flex items-start gap-6">
                {profile.foto && (
                  <div className="flex-shrink-0">
                    <img 
                      src={profile.foto} 
                      alt={profile.nama}
                      className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-800">{profile.nama}</h3>
                  <p className="text-gray-600 mt-1">{profile.email}</p>
                  <div className="flex gap-2 mt-2">
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full font-medium">
                      {profile.role}
                    </span>
                    <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                      profile.verified 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {profile.verified ? 'Terverifikasi' : 'Belum Terverifikasi'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Token Balance Section */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-blue-100 text-sm">Saldo Token</p>
                    <p className="text-3xl font-bold">{profile.tokens_balance || 0} Token</p>
                  </div>
                  <button
                    onClick={() => setShowAdjustBalance(!showAdjustBalance)}
                    className="px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    {showAdjustBalance ? 'Batal' : 'Ubah Saldo'}
                  </button>
                </div>
                
                {/* Adjust Balance Form */}
                {showAdjustBalance && (
                  <form onSubmit={handleAdjustBalance} className="mt-4 bg-white rounded-lg p-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">
                          Jumlah (positif untuk tambah, negatif untuk kurang)
                        </label>
                        <input
                          type="number"
                          value={adjustAmount}
                          onChange={(e) => setAdjustAmount(e.target.value)}
                          placeholder="Contoh: 100 atau -50"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">
                          Alasan Perubahan
                        </label>
                        <input
                          type="text"
                          value={adjustReason}
                          onChange={(e) => setAdjustReason(e.target.value)}
                          placeholder="Contoh: Kompensasi bug, Hadiah event, dll"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={adjusting}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {adjusting ? 'Memproses...' : 'Simpan Perubahan'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Personal Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-bold text-lg mb-3">Informasi Pribadi</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Username:</p>
                    <p className="font-semibold">{profile.username || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">NIK:</p>
                    <p className="font-semibold">{profile.nik || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">No. Telepon:</p>
                    <p className="font-semibold">{profile.no_telp || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Jenis Kelamin:</p>
                    <p className="font-semibold">{profile.m_jeniskelamin?.jeniskelamin || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Tanggal Lahir:</p>
                    <p className="font-semibold">
                      {profile.tgl_lahir ? new Date(profile.tgl_lahir).toLocaleDateString('id-ID') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Kota Lahir:</p>
                    <p className="font-semibold">{profile.m_kota_lahir?.nama_kota || '-'}</p>
                  </div>
                </div>
              </div>

              {/* School Information */}
              {profile.m_sekolah && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-lg mb-3">Informasi Sekolah</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Nama Sekolah:</p>
                      <p className="font-semibold">{profile.m_sekolah.nama_sekolah}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Kota:</p>
                      <p className="font-semibold">{profile.m_sekolah.kota}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Jenjang:</p>
                      <p className="font-semibold">{profile.m_sekolah.jenjang}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Social Media */}
              {(profile.instagram || profile.tiktok) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-lg mb-3">Media Sosial</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {profile.instagram && (
                      <div>
                        <p className="text-gray-600">Instagram:</p>
                        <p className="font-semibold">{profile.instagram}</p>
                      </div>
                    )}
                    {profile.tiktok && (
                      <div>
                        <p className="text-gray-600">TikTok:</p>
                        <p className="font-semibold">{profile.tiktok}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Interest */}
              {profile.m_interest && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-lg mb-3">Minat</h4>
                  <p className="text-sm font-semibold">{profile.m_interest.interest}</p>
                </div>
              )}

              {/* Transaction History */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-bold text-lg mb-3">Riwayat Transaksi Token</h4>
                {loadingTransactions ? (
                  <p className="text-gray-500 text-center py-4">Memuat riwayat...</p>
                ) : transactions.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Belum ada transaksi</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {transactions.map((trans) => (
                      <div 
                        key={trans.id} 
                        className={`border rounded-lg p-3 text-sm ${
                          isAdminTransaction(trans) ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">
                                {(() => {
                                  const adminInfo = getAdminTransactionInfo(trans);
                                  if (adminInfo) {
                                    return adminInfo.type === 'CREDIT' 
                                      ? `+${trans.jumlah_token} Token` 
                                      : `-${trans.jumlah_token} Token`;
                                  }
                                  return `+${trans.jumlah_token} Token`;
                                })()}
                              </span>
                              {isAdminTransaction(trans) && (
                                <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs rounded-full font-medium">
                                  Manual oleh Admin
                                </span>
                              )}
                            </div>
                            <p className="text-gray-500 text-xs mt-1">
                              {trans.t_token_package?.nama || (isAdminTransaction(trans) ? getAdminTransactionInfo(trans)?.reason : trans.order_id)}
                            </p>
                            {isAdminTransaction(trans) && (
                              <p className="text-orange-600 text-xs mt-1">
                                oleh: {getAdminTransactionInfo(trans)?.adminName}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${getTransactionStatusBadge(trans.status)}`}>
                              {trans.status}
                            </span>
                            {trans.harga > 0 && (
                              <p className="text-gray-600 text-xs mt-1">{formatCurrency(trans.harga)}</p>
                            )}
                            <p className="text-gray-400 text-xs mt-1">
                              {new Date(trans.created_at).toLocaleDateString('id-ID', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ==================== TEAM DETAIL MODAL ====================
const TeamDetailModal = ({ team, onClose, token }) => {
  const [showMemberProfile, setShowMemberProfile] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  const handleMemberClick = (memberId) => {
    setSelectedMemberId(memberId);
    setShowMemberProfile(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Detail Tim</h2>
              <p className="text-gray-600 mt-1">{team.nama_tim}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-3xl"
            >
              ×
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Tim Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-3">Informasi Tim</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">Nama Tim:</p>
                  <p className="font-semibold">{team.nama_tim}</p>
                </div>
                <div>
                  <p className="text-gray-600">Nama Sekolah:</p>
                  <p className="font-semibold">{team.nama_sekolah}</p>
                </div>
                <div>
                  <p className="text-gray-600">Penanggungjawab:</p>
                  <p className="font-semibold">{team.penanggungjawab_nama}</p>
                </div>
                <div>
                  <p className="text-gray-600">No. HP Penanggungjawab:</p>
                  <p className="font-semibold">{team.penanggungjawab_hp}</p>
                </div>
              </div>
            </div>

            {/* Event Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-3">Informasi Event</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">Event:</p>
                  <p className="font-semibold">{team.t_registrasi?.m_event?.nama || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Series:</p>
                  <p className="font-semibold">{team.t_registrasi?.m_series?.nama_series || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Kompetisi:</p>
                  <p className="font-semibold">{team.t_registrasi?.m_kompetisi?.nama_kompetisi || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Jenis:</p>
                  <p className="font-semibold">{team.t_registrasi?.m_kompetisi?.jenis || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-3">Dokumen</h3>
              <div className="space-y-2 text-sm">
                {team.surat_rekomendasi_url && (
                  <div>
                    <p className="text-gray-600">Surat Rekomendasi:</p>
                    <a
                      href={team.surat_rekomendasi_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Lihat Dokumen
                    </a>
                  </div>
                )}
                {team.surat_pernyataan_url && (
                  <div>
                    <p className="text-gray-600">Surat Pernyataan:</p>
                    <a
                      href={team.surat_pernyataan_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Lihat Dokumen
                    </a>
                  </div>
                )}
                {team.ktp_official_url && (
                  <div>
                    <p className="text-gray-600">KTP Official:</p>
                    <a
                      href={team.ktp_official_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Lihat Dokumen
                    </a>
                  </div>
                )}
                {team.kartu_pelajar_url && (
                  <div>
                    <p className="text-gray-600">Kartu Pelajar:</p>
                    <a
                      href={team.kartu_pelajar_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Lihat Dokumen
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Team Members */}
            <div className="bg-gray-50 rounded-lg p-4">
              {/* Header with Breakdown */}
              <div className="mb-3">
                <h3 className="font-bold text-lg mb-2">Anggota Tim</h3>
                <div className="flex flex-wrap gap-2 text-sm">
                  {(() => {
                    // Count from my_team_data only
                    const players = team.my_team_data?.filter(m => 
                      m.role === 'player' || m.role === 'dancer' || m.role === 'participant' ||
                      m.role === 'kapten' || m.role === 'siswa'
                    ).length || 0;
                    const officials = team.my_team_data?.filter(m => m.role === 'official').length || 0;
                    const koordinator = team.my_team_data?.filter(m => m.role === 'koordinator_supporter').length || 0;
                    const crew = team.my_team_data?.filter(m => m.role === 'crew').length || 0;
                    const guru = team.my_team_data?.filter(m => m.role === 'guru').length || 0;
                    
                    const total = (team.my_team_data?.length || 0);
                    
                    return (
                      <>
                        {guru > 0 && (
                          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                            Guru ({guru})
                          </span>
                        )}
                        {players > 0 && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                            Player ({players})
                          </span>
                        )}
                        {officials > 0 && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                            Official ({officials})
                          </span>
                        )}
                        {koordinator > 0 && (
                          <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                            Koor. Supporter ({koordinator})
                          </span>
                        )}
                        {crew > 0 && (
                          <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full font-medium">
                            Crew ({crew})
                          </span>
                        )}
                        <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full font-semibold">
                          Total ({total})
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>
              
              <div className="grid gap-3">
                {/* Display ALL members from my_team_data */}
                {team.my_team_data && team.my_team_data.length > 0 ? (
                  <>
                    {team.my_team_data.map((member, index) => (
                      <div 
                        key={index} 
                        className={`p-3 rounded border hover:border-purple-300 transition-all ${
                          member.isCurrentUser 
                            ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200' 
                            : 'bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <button
                                onClick={() => handleMemberClick(member.m_personal_id || member.id)}
                                className="font-semibold text-purple-600 hover:text-purple-700 hover:underline text-left"
                              >
                                {member.nama}
                              </button>
                              {member.isCurrentUser && (
                                <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full font-bold">
                                  PENDAFTAR
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{member.email}</p>
                            <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-semibold uppercase">
                              {member.role}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">
                            Klik nama untuk lihat profil
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">
                    Belum ada anggota tim
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Member Profile Modal */}
      {showMemberProfile && selectedMemberId && (
        <MemberProfileModal
          userId={selectedMemberId}
          token={token}
          onClose={() => {
            setShowMemberProfile(false);
            setSelectedMemberId(null);
          }}
        />
      )}
    </>
  );
};

// ==================== USER MANAGEMENT ====================
const UserManagement = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterVerified, setFilterVerified] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch data when filters or page changes
  useEffect(() => {
    fetchUsers();
  }, [filterRole, filterVerified, searchTerm, currentPage]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/users?page=${currentPage}&limit=${itemsPerPage}`;
      if (filterRole) url += `&role=${filterRole}`;
      if (filterVerified !== '') url += `&verified=${filterVerified}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.data || []);
      setTotalItems(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  };

  const openProfileModal = (userId) => {
    setSelectedUserId(userId);
    setShowProfileModal(true);
  };

  const handleExportExcel = async () => {
    try {
      toast.loading('Mengunduh data...');
      let url = `${API}/admin/export/users?`;
      if (filterRole) url += `role=${filterRole}&`;
      if (filterVerified !== '') url += `verified=${filterVerified}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `data_user_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.dismiss();
      toast.success('Data berhasil diunduh');
    } catch (error) {
      toast.dismiss();
      toast.error('Gagal mengunduh data');
      console.error('Export error:', error);
    }
  };

  // Pagination numbers
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <div>
      {/* Header with Export Button */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Kelola User</h2>
          <p className="text-sm text-gray-500">Total: {totalItems.toLocaleString()} user</p>
        </div>
        <button
          onClick={handleExportExcel}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export Excel
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter Role
            </label>
            <select
              value={filterRole}
              onChange={(e) => { setFilterRole(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Semua Role</option>
              <option value="UMUM">UMUM</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter Status Verifikasi
            </label>
            <select
              value={filterVerified}
              onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Semua Status</option>
              <option value="true">Terverifikasi</option>
              <option value="false">Belum Terverifikasi</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cari User
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Ketik untuk mencari nama, email, username..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              />
              <svg 
                className="absolute left-3 top-3 w-4 h-4 text-gray-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {loading && searchInput && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-600">
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-500">Memuat data...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
          {searchTerm ? 'Tidak ada user yang cocok dengan pencarian' : 'Tidak ada user'}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">No</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nama</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sekolah</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user, index) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-800">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-800 font-semibold">{user.nama}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{user.username || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {user.m_sekolah?.nama_sekolah || '-'}
                        {user.m_sekolah && (
                          <span className="text-xs text-gray-500 block">
                            {user.m_sekolah.jenjang} • {user.m_sekolah.kota}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'ADMIN' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                          user.verified 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {user.verified ? 'Verified' : 'Unverified'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => openProfileModal(user.id)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-all"
                        >
                          Detail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                Menampilkan {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} dari {totalItems.toLocaleString()} user
              </div>
              <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              
              {pageNumbers.map((number, index) => (
                <button
                  key={index}
                  onClick={() => typeof number === 'number' && setCurrentPage(number)}
                  disabled={number === '...'}
                  className={`px-4 py-2 border rounded-lg ${
                    currentPage === number
                      ? 'bg-ltb-bright text-white'
                      : number === '...'
                      ? 'cursor-default'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {number}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Profile Modal */}
      {showProfileModal && selectedUserId && (
        <MemberProfileModal
          userId={selectedUserId}
          token={token}
          onClose={() => {
            setShowProfileModal(false);
            setSelectedUserId(null);
          }}
        />
      )}
    </div>
  );
};

// ==================== SEKOLAH MANAGEMENT ====================
const SekolahManagement = ({ token }) => {
  const [sekolah, setSekolah] = useState([]);
  const [filteredSekolah, setFilteredSekolah] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [seriesList, setSeriesList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    nama_sekolah: '',
    kota: '',
    jenjang: 'SMA',
    alamat: ''
  });

  useEffect(() => {
    fetchSekolah();
    fetchSeriesList();
  }, []);

  useEffect(() => {
    // Filter sekolah based on search term
    if (searchTerm.trim() === '') {
      setFilteredSekolah(sekolah);
    } else {
      const filtered = sekolah.filter(s => 
        s.nama_sekolah.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSekolah(filtered);
    }
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, sekolah]);

  const fetchSekolah = async () => {
    try {
      const response = await axios.get(`${API}/schools`);
      setSekolah(response.data);
      setFilteredSekolah(response.data);
    } catch (error) {
      console.error('Error fetching sekolah:', error);
      toast.error('Gagal memuat data sekolah');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeriesList = async () => {
    try {
      const response = await axios.get(`${API}/series`);
      setSeriesList(response.data);
    } catch (error) {
      console.error('Error fetching series:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const sekolahData = {
        nama_sekolah: formData.nama_sekolah,
        kota: formData.kota,
        jenjang: formData.jenjang,
        alamat: formData.alamat || '',
        propinsi: 'Jawa Timur' // Default
      };

      if (editMode) {
        // Update existing sekolah
        await axios.put(`${API}/admin/sekolah/${editId}`, sekolahData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sekolah berhasil diupdate');
      } else {
        // Create new sekolah
        await axios.post(`${API}/admin/sekolah`, sekolahData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sekolah berhasil ditambahkan');
      }
      
      setShowForm(false);
      setEditMode(false);
      setEditId(null);
      resetForm();
      fetchSekolah();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data sekolah');
    }
  };

  const handleEdit = (sekolahItem) => {
    setEditMode(true);
    setEditId(sekolahItem.id);
    setFormData({
      nama_sekolah: sekolahItem.nama_sekolah,
      kota: sekolahItem.kota || '',
      jenjang: sekolahItem.jenjang || 'SMA',
      alamat: sekolahItem.alamat || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      nama_sekolah: '',
      kota: '',
      jenjang: 'SMA',
      alamat: ''
    });
    setEditMode(false);
    setEditId(null);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSekolah.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSekolah.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Add/Edit Button */}
      {showForm ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {editMode ? 'Edit Sekolah' : 'Tambah Sekolah Baru'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Sekolah <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nama_sekolah}
                onChange={(e) => setFormData({ ...formData, nama_sekolah: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                placeholder="Contoh: SMAN 1 Surabaya"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kota <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.kota}
                onChange={(e) => setFormData({ ...formData, kota: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                required
              >
                <option value="">Pilih kota</option>
                {[...new Set(seriesList.map(s => s.kota).filter(Boolean))].sort().map(kota => (
                  <option key={kota} value={kota}>{kota}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jenjang <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.jenjang}
                onChange={(e) => setFormData({ ...formData, jenjang: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                required
              >
                <option value="SD">SD</option>
                <option value="SMP">SMP</option>
                <option value="SMA">SMA</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alamat (Opsional)
              </label>
              <textarea
                value={formData.alamat}
                onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                rows="3"
                placeholder="Alamat lengkap sekolah"
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium transition-all"
              >
                {editMode ? 'Update Sekolah' : 'Simpan Sekolah'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-all"
              >
                Batal
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium transition-all flex items-center"
          >
            <span className="mr-2">+</span>
            Tambah Sekolah
          </button>
        </div>
      )}

      {/* Schools Table with Search and Pagination */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">
            Daftar Sekolah ({filteredSekolah.length})
          </h3>
          
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Cari nama sekolah..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
            />
            <svg 
              className="absolute left-3 top-3 w-4 h-4 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        
        {filteredSekolah.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {searchTerm ? 'Tidak ada sekolah yang cocok dengan pencarian' : 'Belum ada data sekolah'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">No</th>
                    <th className="px-4 py-3 text-left font-semibold">Nama Sekolah</th>
                    <th className="px-4 py-3 text-left font-semibold">Kota</th>
                    <th className="px-4 py-3 text-left font-semibold">Jenjang</th>
                    <th className="px-4 py-3 text-left font-semibold">Alamat</th>
                    <th className="px-4 py-3 text-center font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((s, index) => (
                    <tr key={s.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">{indexOfFirstItem + index + 1}</td>
                      <td className="px-4 py-3 font-medium">{s.nama_sekolah}</td>
                      <td className="px-4 py-3">{s.kota}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                          {s.jenjang}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate">
                        {s.alamat || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(s)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                        >
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex justify-center items-center space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ← Prev
                  </button>
                  
                  <div className="flex space-x-1">
                    {getPageNumbers(currentPage, totalPages).map((pageNum, idx) => (
                      pageNum === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-3 py-1 text-gray-400">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`px-3 py-1 border rounded-lg ${
                            currentPage === pageNum
                              ? 'bg-ltb-bright text-white border-ltb-bright'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    ))}
                  </div>
                  
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                </div>
                
                <div className="text-center text-sm text-gray-500 mt-2">
                  Menampilkan {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredSekolah.length)} dari {filteredSekolah.length} sekolah
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


// ==================== VIDEO MANAGEMENT ====================
const VideoManagement = ({ token }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    youtube_url: '',
    caption: '',
    is_live: false
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data.data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Gagal memuat data video');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.youtube_url.trim()) {
      toast.error('URL YouTube wajib diisi');
      return;
    }
    
    try {
      if (editMode) {
        await axios.put(`${API}/admin/videos/${editId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video berhasil diupdate');
      } else {
        await axios.post(`${API}/admin/videos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video berhasil ditambahkan');
      }
      
      setShowForm(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan video');
    }
  };

  const handleEdit = (video) => {
    setEditMode(true);
    setEditId(video.id);
    setFormData({
      youtube_url: video.youtube_url,
      caption: video.caption,
      is_live: video.is_live
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus video ini?')) return;
    
    try {
      await axios.delete(`${API}/admin/videos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video berhasil dihapus');
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Gagal menghapus video');
    }
  };

  const handleToggleActive = async (video) => {
    try {
      await axios.put(`${API}/admin/videos/${video.id}`, 
        { aktif: !video.aktif },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(video.aktif ? 'Video dinonaktifkan' : 'Video diaktifkan');
      fetchVideos();
    } catch (error) {
      console.error('Error toggling video:', error);
      toast.error('Gagal mengubah status video');
    }
  };

  const resetForm = () => {
    setFormData({ youtube_url: '', caption: '', is_live: false });
    setEditMode(false);
    setEditId(null);
  };

  const extractYoutubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
      /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ltb-bright"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Daftar Video</h3>
          <p className="text-sm text-gray-500">Kelola video live streaming dan rekaman</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-ltb-bright hover:bg-ltb-dark text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Tambah Video</span>
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editMode ? 'Edit Video' : 'Tambah Video Baru'}
              </h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL YouTube <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
                  placeholder="https://www.youtube.com/watch?v=... atau https://youtube.com/live/..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Mendukung: youtube.com/watch, youtu.be, youtube.com/live, youtube.com/shorts
                </p>
              </div>

              {/* Preview */}
              {formData.youtube_url && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-2">Preview:</p>
                  <img 
                    src={`https://img.youtube.com/vi/${extractYoutubeId(formData.youtube_url)}/mqdefault.jpg`}
                    alt="Preview"
                    className="w-full max-w-xs rounded"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Caption <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.caption}
                  onChange={(e) => setFormData({...formData, caption: e.target.value})}
                  placeholder="Judul atau deskripsi video..."
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                  required
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_live"
                  checked={formData.is_live}
                  onChange={(e) => setFormData({...formData, is_live: e.target.checked})}
                  className="w-4 h-4 text-ltb-bright border-gray-300 rounded focus:ring-ltb-bright"
                />
                <label htmlFor="is_live" className="text-sm text-gray-700">
                  🔴 Video Live Streaming
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-ltb-bright text-white rounded-lg hover:bg-ltb-dark"
                >
                  {editMode ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Videos List */}
      {videos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="text-6xl mb-4">🎬</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Belum Ada Video</h3>
          <p className="text-gray-500">Klik "Tambah Video" untuk menambahkan video pertama</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((video) => (
            <div key={video.id} className={`bg-white rounded-xl shadow-md overflow-hidden ${!video.aktif ? 'opacity-60' : ''}`}>
              {/* Thumbnail */}
              <div className="relative">
                <img 
                  src={video.thumbnail}
                  alt={video.caption}
                  className="w-full h-40 object-cover"
                  onError={(e) => { 
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180" fill="%23374151"><rect width="320" height="180"/><text x="50%" y="50%" text-anchor="middle" fill="%239CA3AF" font-size="14">Video</text></svg>';
                  }}
                />
                {video.is_live && (
                  <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    <span>LIVE</span>
                  </span>
                )}
                {!video.aktif && (
                  <span className="absolute top-2 right-2 bg-gray-600 text-white text-xs px-2 py-1 rounded">
                    Nonaktif
                  </span>
                )}
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-4">
                <h4 className="font-semibold text-gray-800 line-clamp-2 mb-2">{video.caption}</h4>
                <p className="text-xs text-gray-500 truncate mb-3">{video.youtube_url}</p>
                
                {/* Actions */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleToggleActive(video)}
                    className={`text-xs px-3 py-1 rounded-full ${
                      video.aktif 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {video.aktif ? '✓ Aktif' : '○ Nonaktif'}
                  </button>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(video)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(video.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      🗑️ Hapus
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== FAQ MANAGEMENT ====================
const FAQManagement = ({ token }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    pertanyaan: '',
    jawaban: '',
    kategori: 'Umum',
    urutan: 1
  });

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/admin/faq`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Gagal memuat data FAQ');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editMode) {
        // Update FAQ
        await axios.put(`${API}/admin/faq/${editId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('FAQ berhasil diupdate');
      } else {
        // Create FAQ
        await axios.post(`${API}/admin/faq`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('FAQ berhasil ditambahkan');
      }
      
      setShowForm(false);
      setEditMode(false);
      setEditId(null);
      resetForm();
      fetchFAQs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan FAQ');
    }
  };

  const handleEdit = (faq) => {
    setEditMode(true);
    setEditId(faq.id);
    setFormData({
      pertanyaan: faq.pertanyaan,
      jawaban: faq.jawaban,
      kategori: faq.kategori || 'Umum',
      urutan: faq.urutan
    });
    setShowForm(true);
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Yakin ingin menghapus FAQ ini?')) return;
    
    try {
      await axios.delete(`${API}/admin/faq/${faqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('FAQ berhasil dihapus');
      fetchFAQs();
    } catch (error) {
      toast.error('Gagal menghapus FAQ');
    }
  };

  const handleToggle = async (faqId) => {
    try {
      await axios.put(`${API}/admin/faq/${faqId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status FAQ berhasil diubah');
      fetchFAQs();
    } catch (error) {
      toast.error('Gagal mengubah status FAQ');
    }
  };

  const resetForm = () => {
    setFormData({
      pertanyaan: '',
      jawaban: '',
      kategori: 'Umum',
      urutan: 1
    });
    setEditMode(false);
    setEditId(null);
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Add/Edit Button */}
      {showForm ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {editMode ? 'Edit FAQ' : 'Tambah FAQ Baru'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kategori <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.kategori}
                  onChange={(e) => setFormData({ ...formData, kategori: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                  required
                >
                  <option value="Umum">Umum</option>
                  <option value="Pendaftaran">Pendaftaran</option>
                  <option value="Event">Event</option>
                  <option value="Tim">Tim</option>
                  <option value="Pembayaran">Pembayaran</option>
                  <option value="Teknis">Teknis</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Urutan <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.urutan}
                  onChange={(e) => setFormData({ ...formData, urutan: parseInt(e.target.value) })}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pertanyaan <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.pertanyaan}
                onChange={(e) => setFormData({ ...formData, pertanyaan: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                placeholder="Contoh: Bagaimana cara mendaftar?"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jawaban <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <MDEditor
                  value={formData.jawaban}
                  onChange={(content) => setFormData({ ...formData, jawaban: content || '' })}
                  preview="edit"
                  height={300}
                  data-color-mode="light"
                  hideToolbar={false}
                  visibleDragBar={false}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Gunakan Markdown atau HTML untuk format teks
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium transition-all"
              >
                {editMode ? 'Update FAQ' : 'Simpan FAQ'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-all"
              >
                Batal
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:bg-ltb-medium transition-all flex items-center"
          >
            <span className="mr-2">+</span>
            Tambah FAQ
          </button>
        </div>
      )}

      {/* FAQ List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">
          Daftar FAQ ({faqs.length})
        </h3>
        
        {faqs.length === 0 ? (
          <p className="text-center text-gray-500 py-8">Belum ada FAQ</p>
        ) : (
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                        {faq.kategori}
                      </span>
                      <span className="text-xs text-gray-500">Urutan: {faq.urutan}</span>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        faq.aktif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {faq.aktif ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </div>
                    <h4 className="text-base font-bold text-gray-800 mb-2">
                      {faq.pertanyaan}
                    </h4>
                    <div 
                      className="text-sm text-gray-600 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: faq.jawaban }}
                    />
                  </div>
                </div>
                
                <div className="flex space-x-2 mt-3 pt-3 border-t">
                  <button
                    onClick={() => handleEdit(faq)}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700 transition-all"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleToggle(faq.id)}
                    className={`px-3 py-1 rounded text-sm font-semibold transition-all ${
                      faq.aktif 
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {faq.aktif ? 'Nonaktifkan' : 'Aktifkan'}
                  </button>
                  <button
                    onClick={() => handleDelete(faq.id)}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm font-semibold hover:bg-red-700 transition-all"
                  >
                    🗑️ Hapus
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Gallery Management Component
const GalleryManagement = ({ token }) => {
  const [photos, setPhotos] = useState([]);
  const [driveFiles, setDriveFiles] = useState(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null);
  const [estimateData, setEstimateData] = useState(null);
  const [estimating, setEstimating] = useState(false);

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [photosRes, storageRes] = await Promise.all([
        axios.get(`${API}/admin/gallery/photos`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/gallery/storage-info`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPhotos(photosRes.data.data || []);
      setStorageInfo(storageRes.data);
    } catch (error) {
      console.error('Error fetching gallery data:', error);
      toast.error('Gagal memuat data galeri');
    } finally {
      setLoading(false);
    }
  };

  const fetchDriveFiles = async () => {
    try {
      const response = await axios.get(`${API}/admin/gallery/drive-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriveFiles(response.data);
    } catch (error) {
      console.error('Error fetching drive files:', error);
      toast.error('Gagal memuat file dari Google Drive');
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleEstimate = async (forceResync = false) => {
    if (syncing) return;
    setEstimating(true);
    setEstimateData(null);
    try {
      const response = await axios.post(
        `${API}/admin/gallery/sync/estimate?force_resync_originals=${forceResync}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEstimateData({ ...response.data, forceResync });
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Gagal menghitung estimasi: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setEstimating(false);
    }
  };

  const handleStartSync = async () => {
    if (!estimateData) return;
    const forceResync = estimateData.forceResync;
    setSyncing(true);
    setSyncStats(null);
    setSyncProgress({ current: 0, total: estimateData.total_to_process, percent: 0, current_file: '' });
    setEstimateData(null);

    try {
      await axios.post(
        `${API}/admin/gallery/sync/start?force_resync_originals=${forceResync}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      pollProgress();
    } catch (error) {
      setSyncing(false);
      setSyncProgress(null);
      toast.error('Gagal memulai sync: ' + (error.response?.data?.detail || error.message));
    }
  };

  const pollProgress = () => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/gallery/sync/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res.data;
        setSyncProgress({
          current: data.current,
          total: data.total,
          percent: data.percent,
          current_file: data.current_file
        });

        if (!data.running) {
          clearInterval(interval);
          setSyncing(false);
          if (data.stats) {
            setSyncStats(data.stats);
            const newCount = data.stats.newly_synced || 0;
            const resyncCount = data.stats.originals_resynced || 0;
            if (data.stats.error) {
              toast.error('Sync gagal: ' + data.stats.error);
            } else {
              toast.success(`Selesai! ${newCount} foto baru, ${resyncCount} original re-synced.`);
            }
          }
          setSyncProgress(null);
          fetchData();
          fetchDriveFiles();
        }
      } catch {
        clearInterval(interval);
        setSyncing(false);
        setSyncProgress(null);
      }
    }, 2000);
  };

  const handleToggle = async (photoId) => {
    try {
      await axios.put(`${API}/admin/gallery/photos/${photoId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status foto diubah');
      fetchData();
    } catch (error) {
      console.error('Error toggling photo:', error);
      toast.error('Gagal mengubah status foto');
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Yakin ingin menghapus foto ini? Foto juga akan dihapus dari storage.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/gallery/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Foto berhasil dihapus');
      fetchData();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Gagal menghapus foto');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data galeri...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">🖼️ Kelola Galeri Foto</h2>
      
      {/* Storage Info Card */}
      {storageInfo && (
        <div className={`rounded-xl p-4 ${
          storageInfo.critical ? 'bg-red-50 border border-red-300' :
          storageInfo.warning ? 'bg-yellow-50 border border-yellow-300' :
          'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Penggunaan Storage</h3>
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
              storageInfo.critical ? 'bg-red-500 text-white' :
              storageInfo.warning ? 'bg-yellow-500 text-white' :
              'bg-green-500 text-white'
            }`}>
              {storageInfo.usage_percent}%
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
            <div 
              className={`h-3 rounded-full transition-all ${
                storageInfo.critical ? 'bg-red-500' :
                storageInfo.warning ? 'bg-yellow-500' :
                'bg-green-500'
              }`}
              style={{ width: `${Math.min(storageInfo.usage_percent, 100)}%` }}
            ></div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Total Foto</p>
              <p className="font-bold text-gray-800">{storageInfo.total_photos || photos.length}</p>
            </div>
            <div>
              <p className="text-gray-500">Terpakai</p>
              <p className="font-bold text-gray-800">{formatBytes(storageInfo.total_size_bytes)}</p>
            </div>
            <div>
              <p className="text-gray-500">Tersisa</p>
              <p className="font-bold text-gray-800">{formatBytes(storageInfo.remaining_bytes)}</p>
            </div>
            <div>
              <p className="text-gray-500">Limit</p>
              <p className="font-bold text-gray-800">{formatBytes(storageInfo.storage_limit_bytes)}</p>
            </div>
            <div>
              <p className="text-gray-500">Tanpa Original</p>
              <p className={`font-bold ${storageInfo.missing_originals > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {storageInfo.missing_originals || 0} foto
              </p>
            </div>
          </div>
          
          {storageInfo.critical && (
            <div className="mt-3 p-3 bg-red-100 rounded-lg">
              <p className="text-red-800 font-semibold">⚠️ Peringatan: Storage hampir penuh! Hapus beberapa foto atau upgrade storage.</p>
            </div>
          )}
          
          {storageInfo.warning && !storageInfo.critical && (
            <div className="mt-3 p-3 bg-yellow-100 rounded-lg">
              <p className="text-yellow-800 font-semibold">⚠️ Storage sudah mencapai 80%. Pertimbangkan untuk menghapus foto yang tidak diperlukan.</p>
            </div>
          )}
          
          {storageInfo.missing_originals > 0 && (
            <div className="mt-3 p-3 bg-orange-100 rounded-lg">
              <p className="text-orange-800 font-semibold">
                📦 Ada {storageInfo.missing_originals} foto yang belum memiliki file original. 
                Klik tombol "Re-sync Originals" untuk mengunduh file original agar user bisa download foto setelah pembelian.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Sync Section */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-800">Sinkronisasi dari Google Drive</h3>
            <p className="text-sm text-gray-500">
              Folder: <code className="bg-gray-100 px-2 py-0.5 rounded">13Iwfnty3Ycm7bb97nXKU-3N8FSZOMNDq</code>
            </p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={fetchDriveFiles}
              disabled={syncing}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-all disabled:opacity-50"
            >
              Cek File
            </button>
            <button
              onClick={() => handleEstimate(false)}
              disabled={syncing || estimating || (storageInfo && storageInfo.critical)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {estimating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Menghitung...
                </>
              ) : (
                'Sync Sekarang'
              )}
            </button>
            <button
              onClick={() => handleEstimate(true)}
              disabled={syncing || estimating || (storageInfo && storageInfo.critical)}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="Re-upload file original untuk foto yang belum memiliki file original"
            >
              {estimating ? 'Menghitung...' : 'Re-sync Originals'}
            </button>
          </div>
        </div>

        {/* Estimate Warning */}
        {estimateData && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg" data-testid="sync-estimate">
            <h4 className="font-semibold text-yellow-800 mb-2">Konfirmasi Sinkronisasi</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
              <div>
                <p className="text-gray-500">Total di Drive</p>
                <p className="font-bold">{estimateData.total_drive} foto</p>
              </div>
              <div>
                <p className="text-gray-500">Sudah Sync</p>
                <p className="font-bold text-green-600">{estimateData.already_synced} foto</p>
              </div>
              <div>
                <p className="text-gray-500">Akan Diproses</p>
                <p className="font-bold text-blue-600">{estimateData.total_to_process} foto</p>
              </div>
              <div>
                <p className="text-gray-500">Estimasi Waktu</p>
                <p className="font-bold text-orange-600">
                  {estimateData.estimated_minutes >= 1 
                    ? `~${estimateData.estimated_minutes} menit`
                    : `~${estimateData.estimated_seconds} detik`
                  }
                </p>
              </div>
            </div>
            {estimateData.total_to_process === 0 ? (
              <div className="flex items-center gap-2">
                <p className="text-green-700 font-semibold">Semua foto sudah tersinkronisasi!</p>
                <button onClick={() => setEstimateData(null)} className="ml-auto px-3 py-1 bg-gray-200 rounded-lg text-sm">Tutup</button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <p className="text-yellow-800 text-sm">
                  Proses ini akan berjalan di background. Jangan tutup halaman admin selama sinkronisasi.
                </p>
                <div className="flex gap-2 ml-auto">
                  <button onClick={() => setEstimateData(null)} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-semibold">Batal</button>
                  <button onClick={handleStartSync} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Mulai Sync</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sync Progress Bar */}
        {syncProgress && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="sync-progress">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-blue-800">Sinkronisasi berjalan...</h4>
              <span className="text-sm font-bold text-blue-700">{syncProgress.percent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className="h-3 rounded-full bg-blue-500 transition-all duration-500"
                style={{ width: `${Math.min(syncProgress.percent, 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>{syncProgress.current} / {syncProgress.total} foto</span>
              <span className="truncate max-w-[200px]">{syncProgress.current_file}</span>
            </div>
          </div>
        )}
        
        {/* Drive Files Info */}
        {driveFiles && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Total di Drive</p>
                <p className="font-bold text-gray-800">{driveFiles.total_count} foto</p>
              </div>
              <div>
                <p className="text-gray-500">Sudah Sync</p>
                <p className="font-bold text-green-600">{driveFiles.synced_count} foto</p>
              </div>
              <div>
                <p className="text-gray-500">Belum Sync</p>
                <p className="font-bold text-yellow-600">{driveFiles.unsynced_count} foto</p>
              </div>
              <div>
                <p className="text-gray-500">Ukuran Total</p>
                <p className="font-bold text-gray-800">{formatBytes(driveFiles.total_size_bytes)}</p>
              </div>
            </div>
            
            {driveFiles.unsynced_count > 0 && (
              <p className="mt-3 text-sm text-blue-600">
                ℹ️ Ada {driveFiles.unsynced_count} foto baru yang bisa disinkronkan
              </p>
            )}
          </div>
        )}
        
        {/* Sync Stats */}
        {syncStats && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-2">Hasil Sinkronisasi</h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Total di Drive</p>
                <p className="font-bold">{syncStats.total_drive_files}</p>
              </div>
              <div>
                <p className="text-gray-500">Sudah Ada</p>
                <p className="font-bold">{syncStats.already_synced}</p>
              </div>
              <div>
                <p className="text-gray-500">Baru Ditambahkan</p>
                <p className="font-bold text-green-600">{syncStats.newly_synced}</p>
              </div>
              <div>
                <p className="text-gray-500">Original Re-synced</p>
                <p className="font-bold text-orange-600">{syncStats.originals_resynced || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Gagal</p>
                <p className="font-bold text-red-600">{syncStats.failed}</p>
              </div>
            </div>
            
            {syncStats.errors && syncStats.errors.length > 0 && (
              <div className="mt-3">
                <p className="text-sm text-red-600 font-semibold">Errors:</p>
                <ul className="text-xs text-red-500 list-disc list-inside">
                  {syncStats.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Photos Grid */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <h3 className="font-semibold text-gray-800 mb-4">Daftar Foto ({photos.length})</h3>
        
        {photos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Belum ada foto di galeri</p>
            <p className="text-sm mt-2">Klik "Sync Sekarang" untuk mengambil foto dari Google Drive</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={photo.preview_url}
                    alt={photo.nama_file}
                    className={`w-full h-full object-cover ${!photo.aktif ? 'opacity-50' : ''}`}
                    loading="lazy"
                  />
                </div>
                
                {/* Status Badge */}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                    photo.aktif ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                  }`}>
                    {photo.aktif ? 'Aktif' : 'Nonaktif'}
                  </span>
                  {!photo.original_path && (
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500 text-white" title="File original belum ada di bucket">
                      ⚠️ No Original
                    </span>
                  )}
                </div>
                
                {/* Price Badge */}
                <div className="absolute top-2 right-2">
                  <span className="bg-yellow-500 text-white px-2 py-0.5 rounded text-xs font-bold">
                    {photo.harga} Token
                  </span>
                </div>
                
                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleToggle(photo.id)}
                    className={`p-2 rounded-full ${
                      photo.aktif ? 'bg-gray-500 hover:bg-gray-600' : 'bg-green-500 hover:bg-green-600'
                    } text-white`}
                    title={photo.aktif ? 'Nonaktifkan' : 'Aktifkan'}
                  >
                    {photo.aktif ? '👁️' : '👁️‍🗨️'}
                  </button>
                  <button
                    onClick={() => handleDelete(photo.id)}
                    className="p-2 rounded-full bg-red-500 hover:bg-red-600 text-white"
                    title="Hapus"
                  >
                    🗑️
                  </button>
                </div>
                
                {/* File Name */}
                <p className="text-xs text-gray-600 mt-1 truncate" title={photo.nama_file}>
                  {photo.nama_file}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== TICKET MANAGEMENT ====================

const TicketManagement = ({ token }) => {
  const [activeSubTab, setActiveSubTab] = useState('pricing'); // pricing, redemption, list
  const [sessions, setSessions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [selectedSessionFilter, setSelectedSessionFilter] = useState('all');
  const [loadingList, setLoadingList] = useState(false);
  
  const [formData, setFormData] = useState({
    nama_sesi: '',
    deskripsi: '',
    tanggal_pertandingan: '',
    lokasi: '',
    harga_token: 5,
    kuota: 100,
    kode_prefix: 'TKT'
  });

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/tickets/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Gagal memuat data sesi tiket');
    }
  };

  const fetchPurchases = async (sessionId = null) => {
    try {
      let url = `${API}/admin/tickets/purchases`;
      if (sessionId && sessionId !== 'all') {
        url += `?session_id=${sessionId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(response.data.data || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    }
  };

  const handleRefreshList = async () => {
    setLoadingList(true);
    await fetchPurchases(selectedSessionFilter);
    setLoadingList(false);
    toast.success('Data tiket berhasil diperbarui');
  };

  // Calculate statistics for selected session
  const getSessionStats = () => {
    if (selectedSessionFilter === 'all') {
      const totalRedeemed = purchases.filter(p => p.status === 'redeemed').length;
      const totalActive = purchases.filter(p => p.status === 'active').length;
      const totalKuota = sessions.reduce((sum, s) => sum + (s.kuota || 0), 0);
      const totalTerjual = sessions.reduce((sum, s) => sum + (s.kuota_terpakai || 0), 0);
      
      return {
        redeemed: totalRedeemed,
        active: totalActive,
        kuota: totalKuota,
        terjual: totalTerjual,
        percentage: totalKuota > 0 ? Math.round((totalRedeemed / totalKuota) * 100) : 0
      };
    } else {
      const session = sessions.find(s => s.id === selectedSessionFilter);
      const sessionPurchases = purchases.filter(p => p.m_ticket_session_id === selectedSessionFilter);
      const redeemed = sessionPurchases.filter(p => p.status === 'redeemed').length;
      const active = sessionPurchases.filter(p => p.status === 'active').length;
      const kuota = session?.kuota || 0;
      
      return {
        redeemed,
        active,
        kuota,
        terjual: session?.kuota_terpakai || 0,
        percentage: kuota > 0 ? Math.round((redeemed / kuota) * 100) : 0
      };
    }
  };

  // Get filtered purchases for list tab
  const filteredPurchases = selectedSessionFilter === 'all' 
    ? purchases 
    : purchases.filter(p => p.m_ticket_session_id === selectedSessionFilter);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSessions(), fetchPurchases()]);
      setLoading(false);
    };
    loadData();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editSession) {
        // Don't send kode_prefix when editing - it's readonly
        const { kode_prefix, ...updateData } = formData;
        await axios.put(`${API}/admin/tickets/sessions/${editSession.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sesi tiket berhasil diupdate');
      } else {
        // Validate prefix for new session
        const submitData = {
          ...formData,
          kode_prefix: formData.kode_prefix || 'TKT'
        };
        await axios.post(`${API}/admin/tickets/sessions`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sesi tiket berhasil dibuat');
      }
      
      setShowModal(false);
      setEditSession(null);
      setFormData({
        nama_sesi: '',
        deskripsi: '',
        tanggal_pertandingan: '',
        lokasi: '',
        harga_token: 5,
        kuota: 100,
        kode_prefix: ''
      });
      fetchSessions();
    } catch (error) {
      console.error('Error saving session:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan sesi tiket');
    }
  };

  const handleEdit = (session) => {
    setEditSession(session);
    setFormData({
      nama_sesi: session.nama_sesi,
      deskripsi: session.deskripsi || '',
      tanggal_pertandingan: session.tanggal_pertandingan?.slice(0, 16) || '',
      lokasi: session.lokasi || '',
      harga_token: session.harga_token,
      kuota: session.kuota,
      kode_prefix: session.kode_prefix || 'TKT'  // Include for display only (readonly)
    });
    setShowModal(true);
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Yakin ingin menghapus sesi tiket ini?')) return;
    
    try {
      await axios.delete(`${API}/admin/tickets/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sesi tiket berhasil dihapus');
      fetchSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus sesi tiket');
    }
  };

  const handleToggleActive = async (session) => {
    try {
      await axios.put(`${API}/admin/tickets/sessions/${session.id}`, 
        { aktif: !session.aktif },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sesi tiket ${session.aktif ? 'dinonaktifkan' : 'diaktifkan'}`);
      fetchSessions();
    } catch (error) {
      console.error('Error toggling session:', error);
      toast.error('Gagal mengubah status sesi');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(purchases);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/admin/tickets/purchases?search=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.data || []);
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Gagal mencari tiket');
    } finally {
      setSearching(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) {
      toast.error('Masukkan kode tiket');
      return;
    }
    
    setRedeeming(true);
    try {
      const response = await axios.post(`${API}/admin/tickets/redeem`, 
        { kode_tiket: redeemCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Tiket berhasil ditukarkan untuk ${response.data.buyer_name}`);
      setRedeemCode('');
      fetchPurchases();
      handleSearch();
    } catch (error) {
      console.error('Error redeeming:', error);
      toast.error(error.response?.data?.detail || 'Gagal menukarkan tiket');
    } finally {
      setRedeeming(false);
    }
  };

  const handleDeleteTicket = async (purchase, withRefund = true) => {
    const confirmMsg = withRefund 
      ? `Yakin ingin menghapus tiket ${purchase.kode_tiket}?\n\nToken (${purchase.harga_token || 0}) akan dikembalikan ke pembeli.`
      : `Yakin ingin menghapus tiket ${purchase.kode_tiket} TANPA refund token?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await axios.delete(
        `${API}/admin/tickets/purchases/${purchase.id}?refund_tokens=${withRefund}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.refunded) {
        toast.success(`Tiket ${purchase.kode_tiket} berhasil dihapus. ${response.data.refund_amount} token dikembalikan ke ${response.data.buyer_name}`);
      } else {
        toast.success(`Tiket ${purchase.kode_tiket} berhasil dihapus`);
      }
      
      fetchPurchases();
      fetchSessions(); // Refresh sessions to update quota
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus tiket');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">Aktif</span>;
      case 'redeemed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">Sudah Ditukar</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold">Dibatalkan</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-bold">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data tiket...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">🎫 Kelola Tiket</h2>
      
      {/* Sub Tabs */}
      <div className="bg-white rounded-xl shadow-md p-1 flex">
        <button
          onClick={() => setActiveSubTab('pricing')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
            activeSubTab === 'pricing'
              ? 'bg-blue-500 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          💰 Harga & Sesi
        </button>
        <button
          onClick={() => setActiveSubTab('redemption')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
            activeSubTab === 'redemption'
              ? 'bg-blue-500 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          🎟️ Penukaran
        </button>
        <button
          onClick={() => setActiveSubTab('list')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
            activeSubTab === 'list'
              ? 'bg-blue-500 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          📋 Daftar Tiket
        </button>
      </div>

      {/* Pricing Tab */}
      {activeSubTab === 'pricing' && (
        <div className="space-y-4">
          {/* Add Button */}
          <div className="flex justify-end">
            <button
              onClick={() => {
                setEditSession(null);
                setFormData({
                  nama_sesi: '',
                  deskripsi: '',
                  tanggal_pertandingan: '',
                  lokasi: '',
                  harga_token: 5,
                  kuota: 100,
                  kode_prefix: ''
                });
                setShowModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2"
            >
              + Tambah Sesi Tiket
            </button>
          </div>

          {/* Sessions List */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {sessions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>Belum ada sesi tiket</p>
                <p className="text-sm mt-2">Klik "Tambah Sesi Tiket" untuk membuat sesi baru</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nama Sesi</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tanggal</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Lokasi</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Prefix</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Harga</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Kuota</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sessions.map((session) => (
                      <tr key={session.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{session.nama_sesi}</div>
                          {session.deskripsi && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">{session.deskripsi}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(session.tanggal_pertandingan)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{session.lokasi || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-mono text-sm font-bold">
                            {session.kode_prefix || 'TKT'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm font-bold">
                            {session.harga_token} Token
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm font-medium ${
                            session.kuota_terpakai >= session.kuota ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {session.kuota_terpakai}/{session.kuota}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            session.aktif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {session.aktif ? 'Aktif' : 'Nonaktif'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEdit(session)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleToggleActive(session)}
                              className={`p-2 rounded-lg ${
                                session.aktif ? 'text-gray-600 hover:bg-gray-50' : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={session.aktif ? 'Nonaktifkan' : 'Aktifkan'}
                            >
                              {session.aktif ? '⏸️' : '▶️'}
                            </button>
                            <button
                              onClick={() => handleDelete(session.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Hapus"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Redemption Tab - Quick Redeem Only */}
      {activeSubTab === 'redemption' && (
        <div className="space-y-4">
          {/* Quick Redeem */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="font-semibold text-gray-800 mb-4 text-lg">Tukar Tiket Cepat</h3>
            <p className="text-sm text-gray-500 mb-4">Masukkan kode tiket untuk penukaran langsung</p>
            <div className="flex gap-3">
              <input
                type="text"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                placeholder="Masukkan kode tiket (contoh: TKT-XXXXXXXX)"
                className="flex-1 px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-mono"
                onKeyPress={(e) => e.key === 'Enter' && handleRedeem()}
              />
              <button
                onClick={handleRedeem}
                disabled={redeeming || !redeemCode.trim()}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2 text-lg"
              >
                {redeeming ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  '✓ Tukar'
                )}
              </button>
            </div>
          </div>

          {/* Search Ticket */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="font-semibold text-gray-800 mb-4 text-lg">Cari Tiket</h3>
            <p className="text-sm text-gray-500 mb-4">Cari berdasarkan kode tiket, nama, NIK, atau email</p>
            <div className="flex gap-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Masukkan kata kunci pencarian..."
                className="flex-1 px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleSearch}
                disabled={searching}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold disabled:opacity-50"
              >
                {searching ? '...' : '🔍 Cari'}
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-3">Hasil Pencarian ({searchResults.length})</h4>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {searchResults.map((purchase) => (
                    <div key={purchase.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-blue-600">{purchase.kode_tiket}</span>
                          {getStatusBadge(purchase.status)}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {purchase.m_personal?.nama} • {purchase.m_ticket_session?.nama_sesi}
                        </div>
                      </div>
                      {purchase.status === 'active' && (
                        <button
                          onClick={() => {
                            setRedeemCode(purchase.kode_tiket);
                            handleRedeem();
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold"
                        >
                          Tukar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">💡</span>
              <div>
                <p className="font-medium text-blue-800">Tips Penukaran</p>
                <ul className="text-sm text-blue-700 mt-1 list-disc list-inside">
                  <li>Minta pengunjung menunjukkan QR Code atau kode tiket</li>
                  <li>Scan atau ketik kode tiket di kolom di atas</li>
                  <li>Untuk melihat daftar lengkap, buka tab "Daftar Tiket"</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List Tab - Ticket List with Filter & Stats */}
      {activeSubTab === 'list' && (
        <div className="space-y-4">
          {/* Filter & Refresh */}
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter berdasarkan Sesi</label>
                <select
                  value={selectedSessionFilter}
                  onChange={(e) => setSelectedSessionFilter(e.target.value)}
                  className="w-full sm:w-auto min-w-[250px] px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Semua Sesi</option>
                  {sessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.nama_sesi} ({formatDate(session.tanggal_pertandingan)})
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleRefreshList}
                disabled={loadingList}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold flex items-center gap-2"
              >
                {loadingList ? (
                  <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                Refresh
              </button>
            </div>
          </div>

          {/* Statistics */}
          {(() => {
            const stats = getSessionStats();
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-md p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{stats.redeemed}</div>
                  <div className="text-sm text-gray-500">Sudah Ditukar</div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-4 text-center">
                  <div className="text-3xl font-bold text-yellow-600">{stats.active}</div>
                  <div className="text-sm text-gray-500">Belum Ditukar</div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">{stats.kuota}</div>
                  <div className="text-sm text-gray-500">Kuota Sesi</div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-4 text-center">
                  <div className="text-3xl font-bold text-purple-600">{stats.percentage}%</div>
                  <div className="text-sm text-gray-500">Persentase Ditukar</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all" 
                      style={{ width: `${stats.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Ticket Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Kode Tiket</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Pembeli</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Sesi</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredPurchases.map((purchase) => (
                    <tr key={purchase.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono font-bold text-blue-600">{purchase.kode_tiket}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{purchase.m_personal?.nama || '-'}</div>
                        <div className="text-xs text-gray-500">{purchase.m_personal?.email}</div>
                        {purchase.m_personal?.nik && (
                          <div className="text-xs text-gray-400">NIK: {purchase.m_personal.nik}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium">{purchase.m_ticket_session?.nama_sesi || '-'}</div>
                        <div className="text-xs text-gray-500">
                          {formatDate(purchase.m_ticket_session?.tanggal_pertandingan)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(purchase.status)}
                        {purchase.redeemed_at && (
                          <div className="text-xs text-gray-500 mt-1">
                            {formatDate(purchase.redeemed_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {purchase.status === 'active' && (
                            <button
                              onClick={async () => {
                                setRedeemCode(purchase.kode_tiket);
                                await handleRedeem();
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-sm font-semibold"
                              title="Tukar tiket"
                            >
                              Tukar
                            </button>
                          )}
                          {/* Delete with refund button */}
                          <button
                            onClick={() => handleDeleteTicket(purchase, true)}
                            className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                              purchase.status === 'active' 
                                ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                                : 'bg-red-500 hover:bg-red-600 text-white'
                            }`}
                            title={purchase.status === 'active' ? 'Hapus & Refund Token' : 'Hapus Tiket'}
                          >
                            {purchase.status === 'active' ? 'Refund' : 'Hapus'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredPurchases.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        Belum ada data tiket untuk sesi ini
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Create/Edit Session */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  {editSession ? 'Edit Sesi Tiket' : 'Tambah Sesi Tiket'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nama Sesi *</label>
                  <input
                    type="text"
                    value={formData.nama_sesi}
                    onChange={(e) => setFormData({...formData, nama_sesi: e.target.value})}
                    required
                    placeholder="Contoh: Final Futsal SMP - Sesi 1"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prefix Kode Tiket {editSession ? '(tidak dapat diubah)' : '*'}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={formData.kode_prefix}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
                        setFormData({...formData, kode_prefix: val});
                      }}
                      placeholder="Contoh: FINAL, SMP1, dll"
                      maxLength={5}
                      disabled={!!editSession}
                      className={`w-32 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono uppercase ${
                        editSession ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    />
                    <span className="text-gray-500 font-mono">-XXXXXXXX</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Max 5 karakter alfanumerik. Contoh hasil: {formData.kode_prefix || 'PREFIX'}-ABC12345
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label>
                  <textarea
                    value={formData.deskripsi}
                    onChange={(e) => setFormData({...formData, deskripsi: e.target.value})}
                    placeholder="Deskripsi sesi pertandingan..."
                    rows={3}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal & Waktu Pertandingan *</label>
                  <input
                    type="datetime-local"
                    value={formData.tanggal_pertandingan}
                    onChange={(e) => setFormData({...formData, tanggal_pertandingan: e.target.value})}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lokasi</label>
                  <input
                    type="text"
                    value={formData.lokasi}
                    onChange={(e) => setFormData({...formData, lokasi: e.target.value})}
                    placeholder="Contoh: GOR Sidoarjo"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Harga (Token) *</label>
                    <input
                      type="number"
                      value={formData.harga_token}
                      onChange={(e) => setFormData({...formData, harga_token: parseInt(e.target.value) || 0})}
                      required
                      min={1}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kuota *</label>
                    <input
                      type="number"
                      value={formData.kuota}
                      onChange={(e) => setFormData({...formData, kuota: parseInt(e.target.value) || 0})}
                      required
                      min={1}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                  >
                    {editSession ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TRANSACTION MANAGEMENT ====================
const TransactionManagement = ({ token }) => {
  const [activeSubTab, setActiveSubTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard state
  const [summary, setSummary] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Purchases state
  const [purchases, setPurchases] = useState([]);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesTotalPages, setPurchasesTotalPages] = useState(1);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [purchasesSearch, setPurchasesSearch] = useState('');
  const [purchasesSearchInput, setPurchasesSearchInput] = useState('');
  const [purchasesStatus, setPurchasesStatus] = useState('');
  const [purchasesDateRange, setPurchasesDateRange] = useState({ start: '', end: '' });
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showPurchaseDetail, setShowPurchaseDetail] = useState(false);
  const [purchaseDetail, setPurchaseDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Usage state
  const [usage, setUsage] = useState([]);
  const [usagePage, setUsagePage] = useState(1);
  const [usageTotalPages, setUsageTotalPages] = useState(1);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usageSearch, setUsageSearch] = useState('');
  const [usageSearchInput, setUsageSearchInput] = useState('');
  const [usageType, setUsageType] = useState('');
  const [usageDateRange, setUsageDateRange] = useState({ start: '', end: '' });
  
  const itemsPerPage = 10;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPurchasesSearch(purchasesSearchInput);
      setPurchasesPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [purchasesSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUsageSearch(usageSearchInput);
      setUsagePage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [usageSearchInput]);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeSubTab === 'dashboard') {
      fetchSummary();
    } else if (activeSubTab === 'purchases') {
      fetchPurchases();
    } else if (activeSubTab === 'usage') {
      fetchUsage();
    }
  }, [activeSubTab, dateRange, purchasesPage, purchasesSearch, purchasesStatus, purchasesDateRange, usagePage, usageSearch, usageType, usageDateRange]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/transactions/summary`;
      const params = [];
      if (dateRange.start) params.push(`start_date=${dateRange.start}`);
      if (dateRange.end) params.push(`end_date=${dateRange.end}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      toast.error('Gagal memuat ringkasan transaksi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/transactions/purchases?page=${purchasesPage}&limit=${itemsPerPage}`;
      if (purchasesSearch) url += `&search=${encodeURIComponent(purchasesSearch)}`;
      if (purchasesStatus) url += `&status=${purchasesStatus}`;
      if (purchasesDateRange.start) url += `&start_date=${purchasesDateRange.start}`;
      if (purchasesDateRange.end) url += `&end_date=${purchasesDateRange.end}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(response.data.data || []);
      setPurchasesTotal(response.data.total || 0);
      setPurchasesTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Gagal memuat data pembelian token');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/transactions/usage?page=${usagePage}&limit=${itemsPerPage}`;
      if (usageSearch) url += `&search=${encodeURIComponent(usageSearch)}`;
      if (usageType) url += `&usage_type=${usageType}`;
      if (usageDateRange.start) url += `&start_date=${usageDateRange.start}`;
      if (usageDateRange.end) url += `&end_date=${usageDateRange.end}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data.data || []);
      setUsageTotal(response.data.total || 0);
      setUsageTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Gagal memuat data penggunaan token');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseDetail = async (transactionId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/admin/transactions/purchases/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchaseDetail(response.data);
      setShowPurchaseDetail(true);
    } catch (error) {
      toast.error('Gagal memuat detail transaksi');
      console.error(error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleBulkVerifyMidtrans = async () => {
    if (!window.confirm('Ini akan memverifikasi semua transaksi "Dibatalkan" ke Midtrans dan me-recover yang sebenarnya sudah dibayar. Lanjutkan?')) {
      return;
    }
    
    try {
      toast.loading('Memverifikasi transaksi ke Midtrans...');
      const response = await axios.post(
        `${API}/admin/transactions/bulk-verify-midtrans?status_filter=cancelled&limit=100`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.dismiss();
      
      const result = response.data;
      if (result.recovered > 0) {
        toast.success(`Berhasil me-recover ${result.recovered} transaksi!`);
        
        // Show details
        const details = result.details
          .filter(d => d.action === 'recovered')
          .map(d => `${d.order_id}: +${d.tokens_added} token`)
          .join('\n');
        
        alert(`Recovery berhasil!\n\nTotal di-recover: ${result.recovered} transaksi\nTotal dicek: ${result.total_checked}\nSudah expired: ${result.already_expired}\n\nDetail:\n${details}`);
        
        // Refresh data
        fetchPurchases();
        fetchSummary();
      } else {
        toast.success(`Verifikasi selesai. Tidak ada transaksi yang perlu di-recover.`);
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Gagal memverifikasi transaksi');
      console.error(error);
    }
  };

  const handleVerifySingleTransaction = async (transactionId) => {
    try {
      toast.loading('Memverifikasi ke Midtrans...');
      const response = await axios.post(
        `${API}/admin/transactions/verify-midtrans/${transactionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.dismiss();
      
      const result = response.data;
      if (result.action_taken === 'recovered') {
        toast.success(`Transaksi berhasil di-recover! +${result.tokens_added} token`);
        fetchPurchases();
        fetchSummary();
        setShowPurchaseDetail(false);
      } else {
        toast.success(result.message || 'Verifikasi selesai');
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Gagal memverifikasi transaksi');
      console.error(error);
    }
  };

  const handleExportPurchases = async () => {
    try {
      toast.loading('Mengunduh data pembelian token...');
      let url = `${API}/admin/export/token-purchases?`;
      if (purchasesStatus) url += `status=${purchasesStatus}&`;
      if (purchasesSearch) url += `search=${encodeURIComponent(purchasesSearch)}&`;
      if (purchasesDateRange.start) url += `start_date=${purchasesDateRange.start}&`;
      if (purchasesDateRange.end) url += `end_date=${purchasesDateRange.end}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `pembelian_token_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.dismiss();
      toast.success('Data berhasil diunduh');
    } catch (error) {
      toast.dismiss();
      toast.error('Gagal mengunduh data');
      console.error(error);
    }
  };

  const handleExportUsage = async () => {
    try {
      toast.loading('Mengunduh data penggunaan token...');
      let url = `${API}/admin/export/token-usage?`;
      if (usageType) url += `usage_type=${usageType}&`;
      if (usageSearch) url += `search=${encodeURIComponent(usageSearch)}&`;
      if (usageDateRange.start) url += `start_date=${usageDateRange.start}&`;
      if (usageDateRange.end) url += `end_date=${usageDateRange.end}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `penggunaan_token_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.dismiss();
      toast.success('Data berhasil diunduh');
    } catch (error) {
      toast.dismiss();
      toast.error('Gagal mengunduh data');
      console.error(error);
    }
  };

  const formatLocalTime = (utcTimeStr) => {
    if (!utcTimeStr) return '-';
    try {
      const date = new Date(utcTimeStr);
      return date.toLocaleString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return utcTimeStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const purchasesPageNumbers = getPageNumbers(purchasesPage, purchasesTotalPages);
  const usagePageNumbers = getPageNumbers(usagePage, usageTotalPages);

  return (
    <div>
      {/* Sub-tabs */}
      <div className="bg-white rounded-lg shadow-md mb-4">
        <div className="flex border-b">
          <button
            onClick={() => setActiveSubTab('dashboard')}
            className={`flex-1 px-4 py-3 text-center font-medium transition-colors ${
              activeSubTab === 'dashboard'
                ? 'text-ltb-bright border-b-2 border-ltb-bright bg-blue-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            📊 Dashboard
          </button>
          <button
            onClick={() => setActiveSubTab('purchases')}
            className={`flex-1 px-4 py-3 text-center font-medium transition-colors ${
              activeSubTab === 'purchases'
                ? 'text-ltb-bright border-b-2 border-ltb-bright bg-blue-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            💳 Pembelian Token
          </button>
          <button
            onClick={() => setActiveSubTab('usage')}
            className={`flex-1 px-4 py-3 text-center font-medium transition-colors ${
              activeSubTab === 'usage'
                ? 'text-ltb-bright border-b-2 border-ltb-bright bg-blue-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            🔄 Penggunaan Token
          </button>
        </div>
      </div>

      {/* Dashboard Tab */}
      {activeSubTab === 'dashboard' && (
        <div>
          {/* Date Filter */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dari Tanggal</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sampai Tanggal</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-3 py-2 border rounded-lg"
                />
              </div>
              <button
                onClick={() => setDateRange({ start: '', end: '' })}
                className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
              >
                Reset
              </button>
            </div>
          </div>

          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-500">Memuat data...</p>
            </div>
          ) : summary && (
            <div className="space-y-4">
              {/* Purchase Stats */}
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-lg font-bold text-gray-800 mb-4">📈 Rekap Pembelian Token</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{summary.purchase_stats?.total_transactions || 0}</p>
                    <p className="text-sm text-gray-600">Total Transaksi</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{summary.purchase_stats?.success || 0}</p>
                    <p className="text-sm text-gray-600">Berhasil</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{summary.purchase_stats?.pending || 0}</p>
                    <p className="text-sm text-gray-600">Pending</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{summary.purchase_stats?.cancelled || 0}</p>
                    <p className="text-sm text-gray-600">Dibatalkan</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{formatCurrency(summary.purchase_stats?.total_revenue || 0)}</p>
                    <p className="text-sm text-gray-600">Total Pendapatan</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{(summary.purchase_stats?.total_tokens_sold || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Token Terjual</p>
                  </div>
                </div>
              </div>

              {/* Usage Stats */}
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-lg font-bold text-gray-800 mb-4">🔄 Rekap Penggunaan Token</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-cyan-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-cyan-600">{summary.usage_stats?.unlock_statistik || 0}</p>
                    <p className="text-sm text-gray-600">Unlock Statistik</p>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-pink-600">{summary.usage_stats?.photo_purchases || 0}</p>
                    <p className="text-sm text-gray-600">Beli Foto</p>
                  </div>
                  <div className="bg-violet-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-violet-600">{summary.usage_stats?.ticket_purchases || 0}</p>
                    <p className="text-sm text-gray-600">Beli Tiket</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-orange-600">{summary.usage_stats?.admin_adjustments || 0}</p>
                    <p className="text-sm text-gray-600">Penyesuaian Admin</p>
                  </div>
                  <div className="bg-teal-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-teal-600">{(summary.usage_stats?.total_tokens_used || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Total Token Digunakan</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Purchases Tab */}
      {activeSubTab === 'purchases' && (
        <div>
          {/* Header with Export and Recovery */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Daftar Pembelian Token</h2>
              <p className="text-sm text-gray-500">Total: {purchasesTotal.toLocaleString()} transaksi</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleBulkVerifyMidtrans}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                title="Verifikasi transaksi yang dibatalkan ke Midtrans dan recover yang sudah dibayar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Recovery Transaksi
              </button>
              <button
                onClick={handleExportPurchases}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Excel
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={purchasesStatus}
                  onChange={(e) => { setPurchasesStatus(e.target.value); setPurchasesPage(1); }}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Semua Status</option>
                  <option value="success">Berhasil</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Dibatalkan</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dari Tanggal</label>
                <input
                  type="date"
                  value={purchasesDateRange.start}
                  onChange={(e) => { setPurchasesDateRange(prev => ({ ...prev, start: e.target.value })); setPurchasesPage(1); }}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sampai Tanggal</label>
                <input
                  type="date"
                  value={purchasesDateRange.end}
                  onChange={(e) => { setPurchasesDateRange(prev => ({ ...prev, end: e.target.value })); setPurchasesPage(1); }}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cari</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Nama, email, username, order ID..."
                    value={purchasesSearchInput}
                    onChange={(e) => setPurchasesSearchInput(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border rounded-lg"
                  />
                  <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-500">Memuat data...</p>
            </div>
          ) : purchases.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              Tidak ada data transaksi
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nama</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Username</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sekolah</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Paket</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Harga</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Waktu</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {purchases.map((t, index) => (
                        <tr key={t.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{(purchasesPage - 1) * itemsPerPage + index + 1}</td>
                          <td className="px-4 py-3 text-sm font-semibold">{t.m_personal?.nama || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{t.m_personal?.email || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{t.m_personal?.username || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{t.m_personal?.sekolah_nama || '-'}</td>
                          <td className="px-4 py-3 text-sm">{t.t_token_package?.nama || '-'}</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatCurrency(t.harga)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatLocalTime(t.created_at)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(t.status)}`}>
                              {t.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => fetchPurchaseDetail(t.id)}
                              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                            >
                              Detail
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {purchasesTotalPages > 1 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-gray-600">
                    Menampilkan {(purchasesPage - 1) * itemsPerPage + 1}-{Math.min(purchasesPage * itemsPerPage, purchasesTotal)} dari {purchasesTotal.toLocaleString()} transaksi
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPurchasesPage(prev => Math.max(prev - 1, 1))}
                      disabled={purchasesPage === 1}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← Prev
                    </button>
                    {purchasesPageNumbers.map((number, index) => (
                      <button
                        key={index}
                        onClick={() => typeof number === 'number' && setPurchasesPage(number)}
                        disabled={number === '...'}
                        className={`px-4 py-2 border rounded-lg ${
                          purchasesPage === number ? 'bg-ltb-bright text-white' : number === '...' ? 'cursor-default' : 'hover:bg-gray-100'
                        }`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => setPurchasesPage(prev => Math.min(prev + 1, purchasesTotalPages))}
                      disabled={purchasesPage === purchasesTotalPages}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Usage Tab */}
      {activeSubTab === 'usage' && (
        <div>
          {/* Header with Export */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Daftar Penggunaan Token</h2>
              <p className="text-sm text-gray-500">Total: {usageTotal.toLocaleString()} transaksi</p>
            </div>
            <button
              onClick={handleExportUsage}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Excel
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipe</label>
                <select
                  value={usageType}
                  onChange={(e) => { setUsageType(e.target.value); setUsagePage(1); }}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Semua Tipe</option>
                  <option value="unlock_statistik">Unlock Statistik</option>
                  <option value="photo_purchase">Beli Foto</option>
                  <option value="ticket_purchase">Beli Tiket</option>
                  <option value="admin_adjustment">Penyesuaian Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dari Tanggal</label>
                <input
                  type="date"
                  value={usageDateRange.start}
                  onChange={(e) => { setUsageDateRange(prev => ({ ...prev, start: e.target.value })); setUsagePage(1); }}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sampai Tanggal</label>
                <input
                  type="date"
                  value={usageDateRange.end}
                  onChange={(e) => { setUsageDateRange(prev => ({ ...prev, end: e.target.value })); setUsagePage(1); }}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cari</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Nama, email, username..."
                    value={usageSearchInput}
                    onChange={(e) => setUsageSearchInput(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border rounded-lg"
                  />
                  <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-500">Memuat data...</p>
            </div>
          ) : usage.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              Tidak ada data penggunaan token
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tipe</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nama</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Username</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sekolah</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Token</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Deskripsi</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Waktu</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {usage.map((u, index) => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{(usagePage - 1) * itemsPerPage + index + 1}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                              u.type === 'unlock_statistik' ? 'bg-cyan-100 text-cyan-700' : 
                              u.type === 'photo_purchase' ? 'bg-pink-100 text-pink-700' :
                              u.type === 'ticket_purchase' ? 'bg-violet-100 text-violet-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {u.type_label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold">{u.user?.nama || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.user?.email || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.user?.username || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.user?.sekolah_nama || '-'}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-red-600">-{u.tokens_used}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.description}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatLocalTime(u.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {usageTotalPages > 1 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-gray-600">
                    Menampilkan {(usagePage - 1) * itemsPerPage + 1}-{Math.min(usagePage * itemsPerPage, usageTotal)} dari {usageTotal.toLocaleString()} transaksi
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setUsagePage(prev => Math.max(prev - 1, 1))}
                      disabled={usagePage === 1}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← Prev
                    </button>
                    {usagePageNumbers.map((number, index) => (
                      <button
                        key={index}
                        onClick={() => typeof number === 'number' && setUsagePage(number)}
                        disabled={number === '...'}
                        className={`px-4 py-2 border rounded-lg ${
                          usagePage === number ? 'bg-ltb-bright text-white' : number === '...' ? 'cursor-default' : 'hover:bg-gray-100'
                        }`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => setUsagePage(prev => Math.min(prev + 1, usageTotalPages))}
                      disabled={usagePage === usageTotalPages}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Purchase Detail Modal */}
      {showPurchaseDetail && purchaseDetail && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => { setShowPurchaseDetail(false); setPurchaseDetail(null); }}
        >
          <div 
            className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-ltb-r text-white p-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Detail Transaksi</h3>
              <button onClick={() => { setShowPurchaseDetail(false); setPurchaseDetail(null); }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Order Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Informasi Order</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order ID</span>
                    <span className="font-mono">{purchaseDetail.order_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(purchaseDetail.status)}`}>
                      {purchaseDetail.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Metode Bayar</span>
                    <span>{purchaseDetail.payment_type || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Waktu Transaksi</span>
                    <span>{formatLocalTime(purchaseDetail.created_at)}</span>
                  </div>
                  {purchaseDetail.paid_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Waktu Bayar</span>
                      <span>{formatLocalTime(purchaseDetail.paid_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Informasi Pembeli</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nama</span>
                    <span className="font-semibold">{purchaseDetail.m_personal?.nama || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email</span>
                    <span>{purchaseDetail.m_personal?.email || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Username</span>
                    <span>{purchaseDetail.m_personal?.username || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">No. HP</span>
                    <span>{purchaseDetail.m_personal?.no_telp || '-'}</span>
                  </div>
                  {purchaseDetail.m_personal?.sekolah && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sekolah</span>
                      <span>{purchaseDetail.m_personal.sekolah.nama_sekolah} ({purchaseDetail.m_personal.sekolah.jenjang})</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Package Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Informasi Paket</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paket</span>
                    <span className="font-semibold">{purchaseDetail.t_token_package?.nama || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Jumlah Token</span>
                    <span>{purchaseDetail.jumlah_token}</span>
                  </div>
                  {purchaseDetail.t_token_package?.bonus_token > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bonus Token</span>
                      <span className="text-green-600">+{purchaseDetail.t_token_package.bonus_token}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Harga</span>
                    <span className="font-bold text-lg text-ltb-bright">{formatCurrency(purchaseDetail.harga)}</span>
                  </div>
                </div>
              </div>

              {/* Midtrans Info */}
              {purchaseDetail.midtrans_transaction_id && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Informasi Midtrans</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Transaction ID</span>
                      <span className="font-mono text-xs">{purchaseDetail.midtrans_transaction_id}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Recovery Button for cancelled/pending transactions */}
              {(purchaseDetail.status === 'cancelled' || purchaseDetail.status === 'pending') && (
                <div className="border-t pt-4">
                  <button
                    onClick={() => handleVerifySingleTransaction(purchaseDetail.id)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Verifikasi & Recovery ke Midtrans
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Cek status pembayaran di Midtrans. Jika sudah dibayar, token akan otomatis ditambahkan.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== BAP MANAGEMENT ====================
const BAPManagement = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [bapList, setBapList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingBap, setEditingBap] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Config state
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({
    logo_left_url: '',
    logo_right_url: '',
    competition_name: 'LIGA TENDANG BOLA'
  });
  
  // Form data
  const emptyPlayer = {
    no: 1, name: '', attendance: 'P', jersey_no: '', position: '',
    goals: '', own_goal: '', yellow_card: 0, red_card: 0, foul_half1: '', foul_half2: ''
  };
  
  const emptyTeam = {
    team_name: '', jersey_color: '', gk_color: '', score: 0,
    head_coach: '', assistant_coach: '',
    timeout_half1: false, timeout_half2: false,
    players: Array(12).fill(null).map((_, i) => ({ ...emptyPlayer, no: i + 1 })),
    penalties: Array(10).fill('')
  };
  
  const [formData, setFormData] = useState({
    venue: '',
    match_date: new Date().toISOString().split('T')[0],
    match_no: '',
    kick_off: '',
    competition_name: '',
    team_home: { ...emptyTeam },
    team_away: { ...emptyTeam },
    referee_name: '',
    notes: '',
    signature_home: '',
    signature_away: '',
    signature_referee: '',
    signer_home: '',
    signer_away: '',
    signer_referee: ''
  });

  const itemsPerPage = 10;
  const [activeTeamTab, setActiveTeamTab] = useState('home');
  const sigRefHome = React.useRef(null);
  const sigRefAway = React.useRef(null);
  const sigRefReferee = React.useRef(null);

  // Team search state
  const [teamSearchHome, setTeamSearchHome] = useState('');
  const [teamSearchAway, setTeamSearchAway] = useState('');
  const [teamResultsHome, setTeamResultsHome] = useState([]);
  const [teamResultsAway, setTeamResultsAway] = useState([]);
  const [loadingTeamHome, setLoadingTeamHome] = useState(false);
  const [loadingTeamAway, setLoadingTeamAway] = useState(false);

  // Team search debounce - HOME
  useEffect(() => {
    if (teamSearchHome.length < 2) { setTeamResultsHome([]); return; }
    const timer = setTimeout(async () => {
      setLoadingTeamHome(true);
      try {
        const res = await axios.get(`${API}/admin/bap/teams/search?q=${encodeURIComponent(teamSearchHome)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeamResultsHome(res.data.data || []);
      } catch (e) { setTeamResultsHome([]); }
      setLoadingTeamHome(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [teamSearchHome]);

  // Team search debounce - AWAY
  useEffect(() => {
    if (teamSearchAway.length < 2) { setTeamResultsAway([]); return; }
    const timer = setTimeout(async () => {
      setLoadingTeamAway(true);
      try {
        const res = await axios.get(`${API}/admin/bap/teams/search?q=${encodeURIComponent(teamSearchAway)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeamResultsAway(res.data.data || []);
      } catch (e) { setTeamResultsAway([]); }
      setLoadingTeamAway(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [teamSearchAway]);

  const selectTeam = async (side, team) => {
    try {
      const res = await axios.get(`${API}/admin/bap/teams/${team.id}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      setFormData(prev => ({
        ...prev,
        [side]: {
          ...prev[side],
          team_name: (data.team_name || '').toUpperCase(),
          head_coach: (data.head_coach || prev[side].head_coach || '').toUpperCase(),
          assistant_coach: (data.assistant_coach || prev[side].assistant_coach || '').toUpperCase(),
          players: data.players.map((p, i) => ({
            ...prev[side].players[i],
            no: p.no,
            name: (p.name || '').toUpperCase(),
            attendance: p.attendance || 'P',
          }))
        }
      }));
      if (side === 'team_home') { setTeamSearchHome(''); setTeamResultsHome([]); }
      else { setTeamSearchAway(''); setTeamResultsAway([]); }
      toast.success(`Tim "${data.team_name}" berhasil dimuat (${data.players.filter(p => p.name).length} pemain)`);
    } catch (error) {
      toast.error('Gagal memuat data tim');
    }
  };

  // Debounce BAP list search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchBapList();
    fetchConfig();
  }, [page, search]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/bap/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setConfig({
          logo_left_url: response.data.logo_left_url || '',
          logo_right_url: response.data.logo_right_url || '',
          competition_name: response.data.competition_name || 'LIGA TENDANG BOLA'
        });
      }
    } catch (error) {
      console.error('Error fetching BAP config:', error);
    }
  };

  const fetchBapList = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/bap?page=${page}&limit=${itemsPerPage}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBapList(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      if (error.response?.status !== 500) {
        toast.error('Gagal memuat daftar BAP');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.post(`${API}/admin/bap/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Konfigurasi berhasil disimpan');
      setShowConfig(false);
    } catch (error) {
      toast.error('Gagal menyimpan konfigurasi');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.venue || !formData.team_home.team_name || !formData.team_away.team_name) {
      toast.error('Venue dan nama kedua tim wajib diisi');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        competition_name: formData.competition_name || config.competition_name,
        referee_name: (formData.referee_name || '').toUpperCase(),
        signature_home: sigRefHome.current && !sigRefHome.current.isEmpty() ? sigRefHome.current.toDataURL() : formData.signature_home,
        signature_away: sigRefAway.current && !sigRefAway.current.isEmpty() ? sigRefAway.current.toDataURL() : formData.signature_away,
        signature_referee: sigRefReferee.current && !sigRefReferee.current.isEmpty() ? sigRefReferee.current.toDataURL() : formData.signature_referee,
        signer_home: (formData.signer_home || '').toUpperCase(),
        signer_away: (formData.signer_away || '').toUpperCase(),
        signer_referee: (formData.signer_referee || '').toUpperCase(),
      };
      
      if (editingBap) {
        await axios.put(`${API}/admin/bap/${editingBap.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('BAP berhasil diupdate');
      } else {
        await axios.post(`${API}/admin/bap`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('BAP berhasil disimpan');
      }
      
      setShowForm(false);
      setEditingBap(null);
      resetForm();
      fetchBapList();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Gagal menyimpan BAP';
      toast.error(msg);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (bap) => {
    setEditingBap(bap);
    const ensureTeam = (t) => {
      if (!t) return { ...emptyTeam };
      return {
        ...emptyTeam,
        ...t,
        team_name: (t.team_name || '').toUpperCase(),
        head_coach: (t.head_coach || '').toUpperCase(),
        assistant_coach: (t.assistant_coach || '').toUpperCase(),
        timeout_half1: t.timeout_half1 === true || t.timeout_half1 === 1,
        timeout_half2: t.timeout_half2 === true || t.timeout_half2 === 1,
        players: (t.players || []).map((p, i) => ({
          ...emptyPlayer,
          ...p,
          no: p.no || i + 1,
          name: (p.name || '').toUpperCase(),
          goals: p.goals ?? '',
          own_goal: p.own_goal ?? '',
          foul_half1: p.foul_half1 ?? '',
          foul_half2: p.foul_half2 ?? '',
        })),
        penalties: Array(10).fill('').map((_, i) => (t.penalties?.[i]) || '')
      };
    };
    setFormData({
      venue: bap.venue || '',
      match_date: bap.match_date || '',
      match_no: bap.match_no || '',
      kick_off: bap.kick_off || bap.team_home?.kick_off || '',
      competition_name: bap.competition_name || '',
      team_home: ensureTeam(bap.team_home),
      team_away: ensureTeam(bap.team_away),
      referee_name: (bap.referee_name || '').toUpperCase(),
      notes: bap.notes || '',
      signature_home: bap.team_home?.signature || '',
      signature_away: bap.team_away?.signature || '',
      signature_referee: bap.team_home?.signature_referee || '',
      signer_home: bap.team_home?.signer_name || '',
      signer_away: bap.team_away?.signer_name || '',
      signer_referee: bap.team_home?.signer_referee_name || ''
    });
    setActiveTeamTab('home');
    setShowForm(true);
  };

  const handleDelete = async (bapId) => {
    if (!window.confirm('Yakin ingin menghapus BAP ini?')) return;
    
    try {
      await axios.delete(`${API}/admin/bap/${bapId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('BAP berhasil dihapus');
      fetchBapList();
    } catch (error) {
      toast.error('Gagal menghapus BAP');
      console.error(error);
    }
  };

  const handleDownloadPdf = async (bapId, teamHome, teamAway) => {
    try {
      toast.loading('Generating PDF...');
      const response = await axios.get(`${API}/admin/bap/${bapId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `BAP_${teamHome}_vs_${teamAway}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('PDF berhasil diunduh');
    } catch (error) {
      toast.dismiss();
      toast.error('Gagal generate PDF');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      venue: '',
      match_date: new Date().toISOString().split('T')[0],
      match_no: '',
      kick_off: '',
      competition_name: '',
      team_home: { ...emptyTeam, players: Array(12).fill(null).map((_, i) => ({ ...emptyPlayer, no: i + 1 })) },
      team_away: { ...emptyTeam, players: Array(12).fill(null).map((_, i) => ({ ...emptyPlayer, no: i + 1 })) },
      referee_name: '',
      notes: '',
      signature_home: '',
      signature_away: '',
      signature_referee: '',
      signer_home: '',
      signer_away: '',
      signer_referee: ''
    });
    setTeamSearchHome(''); setTeamResultsHome([]);
    setTeamSearchAway(''); setTeamResultsAway([]);
    setActiveTeamTab('home');
    if (sigRefHome.current) sigRefHome.current.clear();
    if (sigRefAway.current) sigRefAway.current.clear();
    if (sigRefReferee.current) sigRefReferee.current.clear();
  };

  const updateTeamField = (team, field, value) => {
    const nameFields = ['team_name', 'head_coach', 'assistant_coach'];
    const finalValue = nameFields.includes(field) ? value.toUpperCase() : value;
    setFormData(prev => ({
      ...prev,
      [team]: { ...prev[team], [field]: finalValue }
    }));
  };

  const updatePlayerField = (team, playerIndex, field, value) => {
    const finalValue = field === 'name' ? value.toUpperCase() : value;
    setFormData(prev => {
      const newPlayers = [...prev[team].players];
      newPlayers[playerIndex] = { ...newPlayers[playerIndex], [field]: finalValue };
      return {
        ...prev,
        [team]: { ...prev[team], players: newPlayers }
      };
    });
  };

  const updatePenalty = (team, index, value) => {
    setFormData(prev => {
      const newPenalties = [...prev[team].penalties];
      newPenalties[index] = value;
      return {
        ...prev,
        [team]: { ...prev[team], penalties: newPenalties }
      };
    });
  };

  const pageNumbers = getPageNumbers(page, totalPages);

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Berita Acara Pertandingan (BAP)</h2>
          <p className="text-sm text-gray-500">Total: {total} BAP</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowConfig(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Konfigurasi
          </button>
          <button
            onClick={() => { resetForm(); setEditingBap(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-ltb-bright text-white rounded-lg hover:opacity-90"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Tambah BAP
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Cari venue, tim, kompetisi..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full px-4 py-2 pl-10 border rounded-lg"
          />
          <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* BAP List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-500">Memuat data...</p>
        </div>
      ) : bapList.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>Belum ada BAP. Klik "Tambah BAP" untuk membuat baru.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">No</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pertandingan</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Venue</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tanggal</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Skor</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bapList.map((bap, index) => (
                  <tr key={bap.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{(page - 1) * itemsPerPage + index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-sm">
                        {bap.team_home?.team_name || '-'} vs {bap.team_away?.team_name || '-'}
                      </div>
                      <div className="text-xs text-gray-500">{bap.competition_name || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{bap.venue || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{bap.match_date || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="font-bold">
                        {bap.team_home?.score || 0} - {bap.team_away?.score || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleDownloadPdf(bap.id, bap.team_home?.team_name, bap.team_away?.team_name)}
                          className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                          title="Download PDF"
                        >
                          PDF
                        </button>
                        <button
                          onClick={() => handleEdit(bap)}
                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(bap.id)}
                          className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                        >
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                ← Prev
              </button>
              {pageNumbers.map((num, i) => (
                <button
                  key={i}
                  onClick={() => typeof num === 'number' && setPage(num)}
                  className={`px-3 py-1 border rounded ${page === num ? 'bg-ltb-bright text-white' : 'hover:bg-gray-100'}`}
                  disabled={num === '...'}
                >
                  {num}
                </button>
              ))}
              <button
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Config Modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowConfig(false)}>
          <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-ltb-r text-white p-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Konfigurasi BAP</h3>
              <button onClick={() => setShowConfig(false)}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo Kiri</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => setConfig(prev => ({ ...prev, logo_left_url: ev.target.result }));
                    reader.readAsDataURL(file);
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
                {config.logo_left_url && (
                  <img src={config.logo_left_url} alt="Logo Kiri" className="mt-2 h-16 object-contain" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo Kanan</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => setConfig(prev => ({ ...prev, logo_right_url: ev.target.result }));
                    reader.readAsDataURL(file);
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
                {config.logo_right_url && (
                  <img src={config.logo_right_url} alt="Logo Kanan" className="mt-2 h-16 object-contain" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nama Kompetisi Default</label>
                <input
                  type="text"
                  value={config.competition_name}
                  onChange={(e) => setConfig(prev => ({ ...prev, competition_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="LIGA TENDANG BOLA"
                />
              </div>
              <button
                onClick={handleSaveConfig}
                className="w-full px-4 py-2 bg-ltb-bright text-white rounded-lg hover:opacity-90"
              >
                Simpan Konfigurasi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BAP Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto my-4">
            <div className="bg-gradient-ltb-r text-white p-4 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
              <h3 className="text-lg font-bold">{editingBap ? 'Edit BAP' : 'Tambah BAP Baru'}</h3>
              <button onClick={() => { setShowForm(false); setEditingBap(null); }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4">
              {/* Match Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-3">Informasi Pertandingan</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Venue *</label>
                    <input
                      type="text"
                      value={formData.venue}
                      onChange={(e) => setFormData(prev => ({ ...prev, venue: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kick Off</label>
                    <input
                      type="time"
                      value={formData.kick_off || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, kick_off: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal</label>
                    <input
                      type="date"
                      value={formData.match_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, match_date: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Match No</label>
                    <input
                      type="number"
                      value={formData.match_no}
                      onChange={(e) => setFormData(prev => ({ ...prev, match_no: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nama Kompetisi</label>
                    <input
                      type="text"
                      value={formData.competition_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, competition_name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={config.competition_name}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nama Wasit</label>
                    <input
                      type="text"
                      value={formData.referee_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, referee_name: e.target.value.toUpperCase() }))}
                      className="w-full px-3 py-2 border rounded-lg uppercase"
                    />
                  </div>
                </div>
              </div>

              {/* Teams - Tab Layout */}
              <div className="mb-4">
                <div className="flex border-b mb-0">
                  <button
                    type="button"
                    onClick={() => setActiveTeamTab('home')}
                    className={`px-6 py-2 font-semibold text-sm rounded-t-lg ${activeTeamTab === 'home' ? 'bg-blue-100 text-blue-800 border border-b-0 border-blue-200' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid="tab-team-home"
                  >
                    TEAM HOME
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTeamTab('away')}
                    className={`px-6 py-2 font-semibold text-sm rounded-t-lg ${activeTeamTab === 'away' ? 'bg-red-100 text-red-800 border border-b-0 border-red-200' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid="tab-team-away"
                  >
                    TEAM AWAY
                  </button>
                </div>

                {/* Team Content */}
                {['home', 'away'].map(side => {
                  const teamKey = `team_${side}`;
                  const isHome = side === 'home';
                  const bgColor = isHome ? 'bg-blue-50' : 'bg-red-50';
                  const headerBg = isHome ? 'bg-blue-100' : 'bg-red-100';
                  const textColor = isHome ? 'text-blue-800' : 'text-red-800';
                  const teamSearch = isHome ? teamSearchHome : teamSearchAway;
                  const setTeamSearch = isHome ? setTeamSearchHome : setTeamSearchAway;
                  const teamResults = isHome ? teamResultsHome : teamResultsAway;
                  const loadingTeam = isHome ? loadingTeamHome : loadingTeamAway;

                  if (activeTeamTab !== side) return null;

                  return (
                    <div key={side} className={`${bgColor} rounded-b-lg p-4 border border-t-0`}>
                      {/* Team Search */}
                      <div className="relative mb-3">
                        <input
                          type="text"
                          value={teamSearch}
                          onChange={(e) => setTeamSearch(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                          placeholder="Cari tim dari database..."
                          data-testid={`team-search-${side}`}
                        />
                        {loadingTeam && <span className="absolute right-3 top-2.5 text-xs text-gray-400">Mencari...</span>}
                        {teamResults.length > 0 && (
                          <div className="absolute z-20 w-full bg-white border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                            {teamResults.map(t => (
                              <button key={t.id} type="button" onClick={() => selectTeam(teamKey, t)}
                                className={`w-full text-left px-3 py-2 hover:${bgColor} text-sm border-b last:border-0`}
                                data-testid={`team-option-${side}-${t.id}`}>
                                <span className="font-medium">{t.nama_sekolah}</span>
                                <span className="text-gray-500 text-xs ml-2">{t.jenjang} - {t.kota}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Team Info Fields */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Nama Tim *</label>
                          <input type="text" value={formData[teamKey].team_name}
                            onChange={(e) => updateTeamField(teamKey, 'team_name', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded uppercase" required />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Skor</label>
                          <input type="number" value={formData[teamKey].score}
                            onChange={(e) => updateTeamField(teamKey, 'score', parseInt(e.target.value) || 0)}
                            className="w-full px-2 py-1 text-sm border rounded" min="0" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Warna Jersey</label>
                          <input type="text" value={formData[teamKey].jersey_color}
                            onChange={(e) => updateTeamField(teamKey, 'jersey_color', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Warna GK</label>
                          <input type="text" value={formData[teamKey].gk_color}
                            onChange={(e) => updateTeamField(teamKey, 'gk_color', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Head Coach</label>
                          <input type="text" value={formData[teamKey].head_coach}
                            onChange={(e) => updateTeamField(teamKey, 'head_coach', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded uppercase" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Asst Coach</label>
                          <input type="text" value={formData[teamKey].assistant_coach}
                            onChange={(e) => updateTeamField(teamKey, 'assistant_coach', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded uppercase" />
                        </div>
                      </div>

                      {/* Timeout Checkboxes */}
                      <div className="flex items-center gap-6 mb-3">
                        <label className="flex items-center gap-2 text-xs font-medium">
                          <input type="checkbox" checked={!!formData[teamKey].timeout_half1}
                            onChange={(e) => updateTeamField(teamKey, 'timeout_half1', e.target.checked)}
                            className="rounded border-gray-300" />
                          Timeout Babak 1
                        </label>
                        <label className="flex items-center gap-2 text-xs font-medium">
                          <input type="checkbox" checked={!!formData[teamKey].timeout_half2}
                            onChange={(e) => updateTeamField(teamKey, 'timeout_half2', e.target.checked)}
                            className="rounded border-gray-300" />
                          Timeout Babak 2
                        </label>
                      </div>

                      {/* Players Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className={headerBg}>
                            <tr>
                              <th className="px-1 py-1 w-8">No</th>
                              <th className="px-1 py-1">Nama</th>
                              <th className="px-1 py-1 w-12">P/S/A</th>
                              <th className="px-1 py-1 w-10">Jsy</th>
                              <th className="px-1 py-1 w-12">Pos</th>
                              <th className="px-1 py-1 w-20">Gol</th>
                              <th className="px-1 py-1 w-20">OG</th>
                              <th className="px-1 py-1 w-8">KK</th>
                              <th className="px-1 py-1 w-8">KM</th>
                              <th className="px-1 py-1 w-20">F1</th>
                              <th className="px-1 py-1 w-20">F2</th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData[teamKey].players.map((player, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="px-1 py-1 text-center">{idx + 1}</td>
                                <td className="px-1 py-1">
                                  <input type="text" value={player.name}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'name', e.target.value)}
                                    className="w-full px-1 py-0.5 border rounded text-xs uppercase" />
                                </td>
                                <td className="px-1 py-1">
                                  <select value={player.attendance}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'attendance', e.target.value)}
                                    className="w-full px-1 py-0.5 border rounded text-xs">
                                    <option value="P">P</option>
                                    <option value="S">S</option>
                                    <option value="A">A</option>
                                  </select>
                                </td>
                                <td className="px-1 py-1">
                                  <input type="number" value={player.jersey_no}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'jersey_no', e.target.value)}
                                    className="w-10 px-1 py-0.5 border rounded text-xs" />
                                </td>
                                <td className="px-1 py-1">
                                  <select value={player.position}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'position', e.target.value)}
                                    className="w-full px-1 py-0.5 border rounded text-xs">
                                    <option value="">-</option>
                                    <option value="GK">GK</option>
                                    <option value="P">P</option>
                                    <option value="A">A</option>
                                    <option value="F">F</option>
                                  </select>
                                </td>
                                <td className="px-1 py-1">
                                  <input type="text" value={player.goals}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'goals', e.target.value)}
                                    className="w-20 px-1 py-0.5 border rounded text-xs"
                                    title="Menit gol, pisahkan koma" />
                                </td>
                                <td className="px-1 py-1">
                                  <input type="text" value={player.own_goal}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'own_goal', e.target.value)}
                                    className="w-20 px-1 py-0.5 border rounded text-xs"
                                    title="Menit own goal" />
                                </td>
                                <td className="px-1 py-1">
                                  <input type="number" value={player.yellow_card}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'yellow_card', parseInt(e.target.value) || 0)}
                                    className="w-8 px-1 py-0.5 border rounded text-xs" min="0" />
                                </td>
                                <td className="px-1 py-1">
                                  <input type="number" value={player.red_card}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'red_card', parseInt(e.target.value) || 0)}
                                    className="w-8 px-1 py-0.5 border rounded text-xs" min="0" />
                                </td>
                                <td className="px-1 py-1">
                                  <input type="text" value={player.foul_half1}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'foul_half1', e.target.value)}
                                    className="w-20 px-1 py-0.5 border rounded text-xs"
                                    title="Menit foul babak 1" />
                                </td>
                                <td className="px-1 py-1">
                                  <input type="text" value={player.foul_half2}
                                    onChange={(e) => updatePlayerField(teamKey, idx, 'foul_half2', e.target.value)}
                                    className="w-20 px-1 py-0.5 border rounded text-xs"
                                    title="Menit foul babak 2" />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Penalty Shootout */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-3">Penalty Shootout (Opsional)</h4>
                {['team_home', 'team_away'].map(teamKey => (
                  <div key={teamKey} className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">{teamKey === 'team_home' ? 'HOME' : 'AWAY'}</label>
                    <div className="flex flex-wrap items-end gap-1">
                      {formData[teamKey].penalties.map((p, idx) => (
                        <div key={idx} className="flex flex-col items-center">
                          <span className="text-[10px] text-gray-400">{idx + 1}</span>
                          <input
                            type="text"
                            value={p || ''}
                            onChange={(e) => updatePenalty(teamKey, idx, e.target.value.toUpperCase())}
                            className="w-12 h-7 text-center text-xs border rounded bg-white uppercase"
                            maxLength={1}
                            data-testid={`penalty-${teamKey}-${idx}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
                  <input
                    type="text"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Signature Pads */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-3">Tanda Tangan</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Team Home', ref: sigRefHome, key: 'signature_home', nameKey: 'signer_home', placeholder: 'Nama perwakilan' },
                    { label: 'Referee', ref: sigRefReferee, key: 'signature_referee', nameKey: 'signer_referee', placeholder: 'NAMA WASIT' },
                    { label: 'Team Away', ref: sigRefAway, key: 'signature_away', nameKey: 'signer_away', placeholder: 'Nama perwakilan' },
                  ].map(({ label, ref, key, nameKey, placeholder }) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                      <input
                        type="text"
                        value={formData[nameKey] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [nameKey]: e.target.value.toUpperCase() }))}
                        className="w-full px-2 py-1 text-xs border rounded mb-1 uppercase"
                        placeholder={placeholder}
                      />
                      <div className="border rounded-lg bg-white relative" style={{ height: 120 }}>
                        {formData[key] ? (
                          <div className="relative w-full h-full">
                            <img src={formData[key]} alt={label} className="w-full h-full object-contain" />
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, [key]: '' }))}
                              className="absolute top-1 right-1 text-xs bg-red-500 text-white px-2 py-0.5 rounded">Hapus</button>
                          </div>
                        ) : (
                          <SignatureCanvas
                            ref={ref}
                            penColor="black"
                            canvasProps={{ className: 'w-full h-full rounded-lg' }}
                          />
                        )}
                      </div>
                      {!formData[key] && (
                        <button type="button" onClick={() => { if (ref.current) ref.current.clear(); }}
                          className="text-xs text-gray-500 mt-1 hover:text-red-500">Clear</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingBap(null); }}
                  className="px-6 py-2 border rounded-lg hover:bg-gray-100"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-ltb-bright text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? 'Menyimpan...' : (editingBap ? 'Update BAP' : 'Simpan BAP')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminPanelNew;
